import { useState, useRef, useCallback, useEffect, Fragment } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate, useSearchParams } from 'react-router-dom';
import { logEvent, setUserId, setUserProp, logScreen, analytics } from './firebase.js';
import ReactMarkdown from 'react-markdown';
import { CameraService } from './services/camera.js';
import { AIService, SPACE_CATEGORIES, EXTERIOR_STYLES, GARDEN_STYLES, SEASON_OPTIONS, TIME_OF_DAY_OPTIONS } from './services/ai-service.js';
import { buildShoppingUrl, shoppingProvider } from './services/shopping-links.js';
import { getExamplePhotos } from './services/example-photos.js';
import { useSeasonConfig } from './services/season-config.js';
import { AuthService } from './services/auth-service.js';
import { ReferralService } from './services/referral-service.js';
import { DesignService } from './services/design-service.js';
import { FollowService } from './services/follow-service.js';
import { ProfileService } from './services/profile-service.js';
import { CreditsService, useCredits, useCreditConfig } from './services/credits-service.js';
import { ReviewService } from './services/review-service.js';
import { fetchStyleStats, rankUses, customAggregate } from './services/style-stats-service.js';
import { StyleGallery } from './components/StyleGallery.jsx';
import { Gallery } from './pages/Gallery.jsx';
import { Gio } from './pages/Gio.jsx';
import { PushService, OPEN_ROUTE_EVENT } from './services/push-service.js';
import { QueryCache } from './services/cache.js';
import { RevenueCatService } from './services/revenuecat-service.js';
import { shareLink } from './services/share-service.js';
import { isNativeApp, isAndroid, publicOrigin } from './services/platform-service.js';
import { CameraComponent } from './components/Camera.jsx';
import { AnnouncementModal } from './components/AnnouncementModal.jsx';
import { Header } from './components/Header.jsx';
import { MobileHeader } from './components/MobileHeader.jsx';
import { MobileTabBar } from './components/MobileTabBar.jsx';
import { JsSplash } from './components/JsSplash.jsx';
import { CreditModal } from './components/CreditModal.jsx';
import { EditRegionModal } from './components/EditRegionModal.jsx';
import { PlanRoomPicker } from './components/PlanRoomPicker.jsx';
import { PlanRoomResult, planSliderLabels, PLAN_ROOM_ENABLED } from './components/PlanRoomResult.jsx';
import { BeforeAfterSlider } from './components/BeforeAfterSlider.jsx';
import { Onboarding, isOnboardingDone } from './components/Onboarding.jsx'; // App-level mount; self-manages mobile tab tour + create flow visibility
import { WelcomeScreen } from './components/WelcomeScreen.jsx';
import { FeedCard } from './components/FeedCard.jsx';
import { gridImage } from './utils/design-image.js';
import { MyDesigns } from './pages/MyDesigns.jsx';
import { DesignDetail } from './pages/DesignDetail.jsx';
import { ShareView } from './pages/ShareView.jsx';
import { Terms } from './pages/Terms.jsx';
import { Privacy } from './pages/Privacy.jsx';
import { Support } from './pages/Support.jsx';
import { Landing } from './pages/Landing.jsx';
import { LANDING_LANG_ROUTES, landingLangOf, isLandingPath } from './utils/landing-paths.js';
import { RequireApp } from './components/RequireApp.jsx';
import { DeleteAccount } from './pages/DeleteAccount.jsx';
import { Pricing } from './pages/Pricing.jsx';
import { Account } from './pages/Account.jsx';
import { BlockedUsers } from './pages/BlockedUsers.jsx';
import { Invite } from './pages/Invite.jsx';
import { Bookmarks } from './pages/Bookmarks.jsx';
import { Profile } from './pages/Profile.jsx';
import { CollectionPage } from './pages/CollectionPage.jsx';
import { Admin } from './pages/Admin.jsx';
import { useLocale, makeTranslator, LANG_LABELS, randomRenamePlaceholderIndex } from './hooks/useLocale.jsx';
import { useScrollDirection } from './hooks/useScrollDirection.js';
import { useBlockedUids } from './hooks/useBlockedUids.js';
import './styles/main.css';
import { styleThumb, styleThumbFallback } from './utils/style-thumb.js';

const MAX_PHOTOS = 3;
const MAX_ADDITIONAL_REQUEST = 500;
const MAX_CUSTOM_STYLE = 1000;

// Room AI 식 상단 step 진행 표시. Upload (1) → Style (2) 두 단계만 — generate
// 는 자동 진행이라 step bar 빠지고, result 도 별도 화면.
//
// 크레딧 잔액도 이 줄 왼쪽에 같이 태운다. 데스크톱은 .header 의 칩이 그 역할을
// 하는데 .header 는 768px 밑에서 숨겨져서, 모바일엔 잔액을 볼 곳이 없었다.
// 줄을 새로 만들지 않고 여기 얹는 이유: 크레딧만 있는 줄은 화면 위쪽을 통째로
// 먹는데, step 인디케이터 양옆은 어차피 비어 있다.
function StepProgress({ current, total = 2, credits, onCreditsClick, onBack }) {
  const { t } = useLocale();
  // Pro 는 무제한이라 숫자가 의미 없고, loading 중엔 0 이 번쩍였다 바뀌는 걸
  // 피하려고 자리만 비워 둔다 (게스트는 포함 — 시작 크레딧을 여기서 쓴다).
  const showCredits = !!credits && !credits.loading && credits.plan !== 'pro';
  return (
    <div className="step-progress" role="status" aria-label={t('stepLabel', { current, total })}>
      <span className="step-progress-label">{t('stepLabel', { current, total })}</span>
      {/* 칩이 없거나 (데스크톱·Pro·loading) 숨겨져도 이 칸은 남겨야 막대가
          가운데를 지킨다 — 칸이 사라지면 grid 가 막대를 1번 칸으로 당긴다. */}
      <span className="step-progress-side">
        {/* back 은 step 줄 안에 둔다 — 별도 헤더 행으로 빼면 위가 두 줄이 되고
            헤더 auto-hide 중에 크레딧 칩과 겹쳐 잘린다 (MobileHeader 주석 참고).
            데스크톱은 StyleStep 안에 제 back 버튼이 따로 있어 여기선 감춘다. */}
        {onBack && (
          <button
            type="button"
            className="step-progress-back"
            onClick={onBack}
            aria-label="Back"
          >
            <i className="material-symbols-rounded">arrow_back_ios_new</i>
          </button>
        )}
        {showCredits && (
          <button
            type="button"
            className={`header-credit-chip step-progress-credit${credits.credits <= 0 ? ' is-empty' : ''}`}
            onClick={() => onCreditsClick?.()}
            aria-label={t('creditsAria', { n: credits.credits })}
          >
            <i className="material-symbols-rounded">bolt</i>
            <span>{credits.credits}</span>
          </button>
        )}
      </span>
      <div className="step-progress-bars" aria-hidden="true">
        {Array.from({ length: total }).map((_, i) => (
          <div key={i} className={`step-progress-bar${i < current ? ' active' : ''}`} />
        ))}
      </div>
      {/* 오른쪽 빈 칸 — 막대를 화면 정가운데 유지. */}
      <span className="step-progress-side" aria-hidden="true" />
    </div>
  );
}

// ─── Upload Step ───────────────────────────────────────────────────────────────
const UploadStep = ({ selectedImages, onAddImage, onRemoveImage, onCameraClick, onContinue, spaceCategory, onSpaceCategoryChange }) => {
  const { t, lang } = useLocale();
  const fileInputRef = useRef();
  const addMoreRef = useRef();

  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add('drag-over');
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    // Only remove class when leaving the container itself, not a child element
    if (!e.currentTarget.contains(e.relatedTarget)) {
      e.currentTarget.classList.remove('drag-over');
    }
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove('drag-over');
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    files.slice(0, MAX_PHOTOS - selectedImages.length).forEach(f => onAddImage(f));
  };

  const hasPhotos = selectedImages.length > 0;
  const canAddMore = selectedImages.length < MAX_PHOTOS;

  // 드롭존을 눌렀을 때 무엇이 열리나.
  //   웹  — <input type="file">. 브라우저 파일 대화상자라 카메라가 안 뜬다.
  //   네이티브 — @capacitor/camera 의 pickImages (사진첩 전용).
  // 웹 input 으로는 iOS 피커에서 "사진 촬영" 을 숨길 방법이 표준에 없다.
  // 촬영은 아래 Take Photo 버튼이 이미 맡고 있어서 피커에 또 있으면 중복이고,
  // 사진첩을 고르러 온 사람에게 선택지를 하나 더 물어보는 셈이 된다.
  const openPicker = async () => {
    if (!isNativeApp()) { fileInputRef.current?.click(); return; }
    try {
      const { Camera } = await import(/* @vite-ignore */ '@capacitor/camera');
      const remaining = MAX_PHOTOS - selectedImages.length;
      const { photos } = await Camera.pickImages({ quality: 90, limit: remaining });
      for (const ph of (photos || []).slice(0, remaining)) {
        const res = await fetch(ph.webPath);
        await onAddImage(await res.blob());
      }
    } catch (err) {
      // 사용자가 취소하면 plugin 이 throw 한다 — 에러로 취급하지 않는다.
      if (!/cancel/i.test(err?.message || '')) console.warn('pickImages:', err?.message);
    }
  };

  // 예시 사진 — 사진이 없는 사용자도 흐름을 타볼 수 있게. 탭하면 public/ 의
  // 이미지를 받아 직접 올린 것과 똑같이 처리한다 (blob 에 .type/.size 가 있어
  // validateImageFile 이 그대로 통과 — File 로 감쌀 필요 없다).
  // 배열이 빈 카테고리는 섹션 자체가 안 보인다 (services/example-photos.js).
  const examples = getExamplePhotos(spaceCategory);
  const [addingExample, setAddingExample] = useState(null);

  // 목록에 뜬 건 320px 썸네일이고, 실제로 넣는 건 탭한 뒤에 받는 1200px full.
  const handleExampleClick = async (ex) => {
    if (addingExample) return;
    setAddingExample(ex.thumb);
    try {
      const res = await fetch(ex.full);
      if (!res.ok) throw new Error(`example fetch failed: ${res.status}`);
      await onAddImage(await res.blob());
      logEvent(analytics, 'example_photo_selected', { spaceCategory });
    } catch {
      // 예시를 못 받는 건 사용자가 손 쓸 수 없는 일 — 에러를 띄우는 대신
      // 조용히 넘기고 직접 업로드 경로를 그대로 남겨 둔다.
    } finally {
      setAddingExample(null);
    }
  };

  // (닫을 수 있는 힌트 배너는 제거했다 — "어떤 이미지를 받는지" 안내를 본문
  // 아래 잔글씨로 내리면서 dismiss 상태를 들고 있을 이유가 없어졌다.)

  return (
    <div className="upload-step">
      <h3 className="section-title">{t('uploadTitle')}</h3>

      <div className="space-category-toggle">
        {SPACE_CATEGORIES.map(cat => (
          <button
            key={cat}
            type="button"
            className={`space-category-btn ${spaceCategory === cat ? 'active' : ''}`}
            aria-pressed={spaceCategory === cat}
            onClick={() => onSpaceCategoryChange(cat)}
          >
            {t('spaceCategories.' + cat)}
          </button>
        ))}
      </div>
      {!hasPhotos ? (
        // 1단계 첫 진입. 2단계(스타일 선택)와 같은 구조 — 큰 제목 → 칩 → 본문.
        // 예전엔 제목이 upload-area 안에 h3 로 들어가 있고 그 위엔 회색 힌트
        // 배너가 떠 있어서, 화면에 위계가 없고 2단계와 전혀 다른 화면처럼 보였다.
        <>
          <div
            className="upload-area"
            data-tour="create-upload"
            onClick={openPicker}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <i className="material-symbols-rounded upload-icon">add_photo_alternate</i>
            {/* drag 는 데스크톱에서만 되는 얘기라 터치 기기에선 다른 문구.
                JS 로 분기하면 리사이즈·수화 타이밍에 어긋나므로 CSS 로 토글. */}
            <p className="upload-area-label">
              <span className="upload-hint-pointer">{t('uploadHint')}</span>
              <span className="upload-hint-touch">{t('uploadHintTouch')}</span>
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif"
              className="hidden"
              onChange={(e) => { if (e.target.files[0]) onAddImage(e.target.files[0]); e.target.value = ''; }}
            />
          </div>

          <button className="btn btn-secondary btn-block upload-camera-btn" onClick={onCameraClick}>
            <i className="material-symbols-rounded">camera_alt</i>
            {t('takePhoto')}
          </button>

          {/* 어떤 이미지를 받는지 안내. 예전엔 왼쪽에 강조 막대가 붙은 회색
             배너로 화면 위쪽을 차지했는데, 부가 정보라 본문 아래 잔글씨로
             내렸다. 닫기 버튼도 필요 없어졌다. */}
          <p className="upload-accepts-note">{t('uploadAcceptsHint')}</p>

          {examples.length > 0 && (
            <div className="upload-examples">
              <p className="upload-examples-label">{t('examplePhotos')}</p>
              <div className="upload-examples-row">
                {examples.map((ex, i) => (
                  <button
                    key={ex.thumb}
                    type="button"
                    className={`upload-example-thumb${addingExample === ex.thumb ? ' is-loading' : ''}`}
                    onClick={() => handleExampleClick(ex)}
                    disabled={addingExample !== null}
                    aria-label={`${t('examplePhotos')} ${i + 1}`}
                  >
                    <img src={ex.thumb} alt="" loading="lazy" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        // Photos added: show thumbnail grid
        <>
          <h3 className="section-title" style={{ marginBottom: '1.5rem' }}>{t('yourPhotos')}</h3>

          <div
            className={`photo-slots${canAddMore ? ' droppable' : ''}`}
            onDragOver={canAddMore ? handleDragOver : undefined}
            onDragLeave={canAddMore ? handleDragLeave : undefined}
            onDrop={canAddMore ? handleDrop : undefined}
          >
            {selectedImages.map((img, i) => (
              <div key={i} className="photo-slot filled">
                <img src={img.previewUrl} alt={t('altPhoto', { n: i + 1 })} className="slot-thumb" />
                <button
                  className="slot-remove"
                  onClick={() => onRemoveImage(i)}
                  aria-label="Remove photo"
                >
                  <i className="material-symbols-rounded">close</i>
                </button>
                <span className="slot-label">{t('photoLabel', { n: i + 1 })}</span>
              </div>
            ))}

            {canAddMore && (
              <div
                className="photo-slot empty"
                onClick={() => addMoreRef.current?.click()}
              >
                <i className="material-symbols-rounded">add_photo_alternate</i>
                <span>{t('addPhoto')}</span>
                <input
                  ref={addMoreRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif"
                  className="hidden"
                  onChange={(e) => { if (e.target.files[0]) onAddImage(e.target.files[0]); e.target.value = ''; }}
                />
              </div>
            )}
          </div>

          <div style={{ textAlign: 'center', marginTop: '1rem' }}>
            {canAddMore && (
              <>
                <p style={{ color: 'var(--text-muted)', margin: '0.75rem 0', fontSize: '0.875rem' }}>{t('uploadOr')}</p>
                <button className="btn btn-secondary" onClick={onCameraClick} style={{ marginBottom: '0.5rem' }}>
                  <i className="material-symbols-rounded">camera_alt</i>
                  {t('takePhoto')}
                </button>
              </>
            )}
          </div>

          <div className="controls controls-sticky">
            <button className="btn btn-primary" onClick={onContinue}>
              {t('continue')}
              <i className="material-symbols-rounded" style={{ fontSize: '1.1rem' }}>arrow_forward</i>
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// ─── Style Step ────────────────────────────────────────────────────────────────
const StyleStep = ({
  selectedImages,
  selectedStyle,
  onStyleSelect,
  onGenerate,
  onBack,
  customStyle,
  onCustomStyleChange,
  referenceImage,
  onReferenceChange,
  cleanupVariant,
  onCleanupVariantChange,
  layoutVariant,
  onLayoutVariantChange,
  planVariant,
  onPlanVariantChange,
  planStyle,
  onPlanStyleChange,
  planCleanKind,
  onPlanCleanKindChange,
  planRoomMark,
  onPlanRoomMarkChange,
  isGuest = false,
  additionalRequest,
  onAdditionalRequestChange,
  customStyleHistory,
  spaceCategory,
  selectedSeason,
  onSeasonChange,
  selectedTimeOfDay,
  onTimeOfDayChange,
  styleLocked = false,
  creditsAvailable = null,
  creditPrice = 10,
}) => {
  const { t, lang, styleName } = useLocale();
  const isCustom = selectedStyle === 'custom';
  const gridRef = useRef(null);
  const [columnCount, setColumnCount] = useState(1);
  const [showAdditional, setShowAdditional] = useState(false);
  // 커뮤니티 스타일 통계 — 5분 캐시라 재마운트 부담 없음.
  const [communityStats, setCommunityStats] = useState(null);
  useEffect(() => {
    let stale = false;
    fetchStyleStats().then((d) => { if (!stale) setCommunityStats(d); });
    return () => { stale = true; };
  }, []);
  const [showGallery, setShowGallery] = useState(false);
  // 랭킹/갤러리에서 스타일을 고르면 그리드의 해당 카드(커스텀이면 입력창)로
  // 스크롤 — 어디가 선택됐는지 보이고, 추가 텍스트를 바로 넣을 수 있게.
  const focusSelection = (toCustom) => {
    setTimeout(() => {
      const el = document.querySelector(toCustom ? '.custom-style-textarea' : '.style-card.selected');
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (toCustom) el?.focus?.({ preventScroll: true });
    }, 150);
  };
  const pickBuiltin = (key) => { onStyleSelect(key); setShowGallery(false); focusSelection(false); };
  const pickCustom = (prompt) => {
    onStyleSelect('custom');
    onCustomStyleChange(prompt);
    setShowGallery(false);
    focusSelection(true);
  };
  // 커스텀 스타일 공개 정책 1회 공지 — 갤러리 도입 전 사용자도 알아야 한다.
  const navigate = useNavigate();
  const [showShareNotice, setShowShareNotice] = useState(() => {
    try { return !localStorage.getItem('voda_style_notice_seen'); } catch { return false; }
  });
  const dismissShareNotice = () => {
    try { localStorage.setItem('voda_style_notice_seen', '1'); } catch { /* ignore */ }
    setShowShareNotice(false);
  };

  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;
    const measure = () => {
      const cols = getComputedStyle(grid)
        .gridTemplateColumns
        .split(' ')
        .filter(Boolean).length;
      setColumnCount(cols || 1);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(grid);
    return () => ro.disconnect();
  }, []);

  // Reset the opt-in additional-request toggle whenever the selected style changes.
  useEffect(() => {
    setShowAdditional(false);
  }, [selectedStyle]);

  // Pick the right style catalog for the current space category. Custom
  // is interior-only — the other categories rely on a closed style set.
  // styleCatalog() 를 거치므로 시즌 지난 스타일은 여기서도 빠진다.
  useSeasonConfig();
  const styleCatalog = AIService.styleCatalog(spaceCategory);
  const styleI18nPath = spaceCategory === 'exterior' ? 'exteriorStyles'
    : spaceCategory === 'garden' ? 'gardenStyles'
    : 'styles';
  // Mode 카드 (Custom / Reference / Cleanup) 가 일반 스타일 앞에 — interior
  // 카탈로그만. Cleanup 은 sub-option 두 가지 (tidy / empty) — 카드 클릭 시
  // expander 안에서 라디오 선택.
  const rawKeys = Object.keys(styleCatalog);
  const isInterior = spaceCategory === 'interior';
  const MODE_KEYS = ['custom', 'reference', 'floorplan', 'cleanup', 'layout'];
  const styleKeys = isInterior
    ? [...MODE_KEYS, ...rawKeys.filter(k => !MODE_KEYS.includes(k))]
    : rawKeys;
  // 인라인 칩: 최근 30일 사용 횟수(monthlyUses) 기준, 현재 공간 카테고리로 한정.
  // 개별 커스텀 프롬프트는 랭킹에 끼지 않고 "커스텀 스타일" 집계 항목
  // 하나로 선다 — 누르면 갤러리의 커스텀 섹션으로.
  // (styleKeys 선언 뒤에 있어야 한다 — 앞에 두면 TDZ ReferenceError)
  const trendingChips = (() => {
    if (!communityStats) return [];
    const cat = spaceCategory || 'interior';
    const b = communityStats.builtins.filter((s) => {
      const bare = s.id.replace(/^builtin_/, '');
      return styleKeys.includes(bare) && !MODE_KEYS.includes(bare)
        && (s.spaceCategory || 'interior') === cat && rankUses(s) > 0;
    });
    const customsCat = communityStats.customs.filter(
      (s) => (s.spaceCategory || 'interior') === cat && rankUses(s) > 0,
    );
    const entries = [...b];
    if (customsCat.length > 0) {
      entries.push({
        id: '__customAgg__',
        isCustomAgg: true,
        monthlyUses: customAggregate(communityStats, cat, customsCat),
      });
    }
    return entries
      .sort((x, y) => rankUses(y) - rankUses(x))
      .slice(0, 6);
  })();

  const selectedIndex = selectedStyle ? styleKeys.indexOf(selectedStyle) : -1;
  const lastInRow = selectedIndex >= 0
    ? Math.min(
        (Math.floor(selectedIndex / columnCount) + 1) * columnCount - 1,
        styleKeys.length - 1,
      )
    : -1;

  const isReference = selectedStyle === 'reference';
  const isCleanup = selectedStyle === 'cleanup';
  const isLayout = selectedStyle === 'layout';
  const isFloorplan = selectedStyle === 'floorplan';
  const referenceFileInputRef = useRef();
  const [pickingRoom, setPickingRoom] = useState(false);
  // 평면도 → 3D / 방 보기에 입힐 스타일 — 일반 스타일 카드만 (모드 카드 제외)
  const planStyleKeys = rawKeys.filter(k => !MODE_KEYS.includes(k));
  // 평면도는 한 장만 쓴다 (서버도 첫 장만 받는다) — 비용 안내도 한 장 기준.
  const chargedPhotos = isFloorplan ? 1 : selectedImages.length;

  const expander = selectedStyle && (
    isFloorplan ? (
      <div className="style-expander" key="expander-floorplan">
        <h3 className="style-expander-title">{t('planChooseOption')}</h3>
        <div className="cleanup-option-group">
          {(PLAN_ROOM_ENABLED ? ['3d', 'room', 'clean'] : ['3d', 'clean']).map((v) => (
            <label key={v} className={`cleanup-option ${planVariant === v ? 'selected' : ''}`}>
              <input
                type="radio"
                name="plan-variant"
                value={v}
                checked={planVariant === v}
                onChange={() => onPlanVariantChange(v)}
              />
              <span className="cleanup-option-body">
                <span className="cleanup-option-label">{t(`planOption_${v}`)}</span>
                <span className="cleanup-option-desc">{t(`planOption_${v}Desc`)}</span>
              </span>
            </label>
          ))}
        </div>
        {planVariant === 'clean' ? (
          <div className="plan-sub-block">
            <h4 className="plan-sub-title">{t('planCleanKindTitle')}</h4>
            <div className="plan-chip-row">
              {['line', 'color'].map((k) => (
                <button
                  key={k}
                  type="button"
                  className={`plan-chip${planCleanKind === k ? ' selected' : ''}`}
                  onClick={() => onPlanCleanKindChange(k)}
                >
                  {t(`planCleanKind_${k}`)}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="plan-sub-block">
            <h4 className="plan-sub-title">{t('planStyleTitle')}</h4>
            <div className="community-style-row plan-style-row">
              {planStyleKeys.map((k) => (
                <button
                  key={k}
                  type="button"
                  className={`community-mini-card${planStyle === k ? ' selected' : ''}`}
                  onClick={() => onPlanStyleChange(k)}
                  aria-pressed={planStyle === k}
                >
                  <span className="community-mini-thumb">
                    <img src={styleThumb('interior', k)} alt="" loading="lazy" onError={styleThumbFallback('interior', k)} />
                  </span>
                  <span className="community-mini-name">{styleName(k, 'interior')}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        {planVariant === 'room' && (
          <div className="plan-sub-block">
            <h4 className="plan-sub-title">{t('planRoomTitle')}</h4>
            {isGuest ? (
              <p className="style-expander-hint">{t('planRoomSignIn')}</p>
            ) : (
              <>
                {planRoomMark ? (
                  <div className="plan-room-mark">
                    <img src={planRoomMark.previewUrl} alt={t('planRoomPicked')} />
                    <div className="plan-room-mark-actions">
                      <span>{t('planRoomPicked')}</span>
                      <button type="button" className="expander-add-btn" onClick={() => setPickingRoom(true)}>{t('planRoomRepaint')}</button>
                      <button type="button" className="expander-add-btn" onClick={() => onPlanRoomMarkChange(null)}>{t('planRoomClear')}</button>
                    </div>
                  </div>
                ) : (
                  <button type="button" className="btn btn-secondary plan-room-paint-btn" onClick={() => setPickingRoom(true)}>
                    <i className="material-symbols-rounded">brush</i>
                    {t('planRoomPaint')}
                  </button>
                )}
                <input
                  className="edit-field-input plan-room-name"
                  value={additionalRequest}
                  onChange={(e) => onAdditionalRequestChange(e.target.value.slice(0, MAX_ADDITIONAL_REQUEST))}
                  placeholder={t('planRoomNamePlaceholder')}
                  maxLength={MAX_ADDITIONAL_REQUEST}
                />
              </>
            )}
          </div>
        )}
        <p className="style-expander-hint plan-crop-hint">
          <i className="material-symbols-rounded">crop</i>
          {t('planCropHint')}
        </p>
        {selectedImages.length > 1 && <p className="style-expander-hint">{t('planFirstOnly')}</p>}
      </div>
    ) : isCleanup ? (
      <div className="style-expander" key="expander-cleanup">
        <h3 className="style-expander-title">{t('cleanupChooseOption')}</h3>
        <div className="cleanup-option-group">
          <label className={`cleanup-option ${cleanupVariant === 'tidy' ? 'selected' : ''}`}>
            <input
              type="radio"
              name="cleanup-variant"
              value="tidy"
              checked={cleanupVariant === 'tidy'}
              onChange={() => onCleanupVariantChange('tidy')}
            />
            <span className="cleanup-option-body">
              <span className="cleanup-option-label">{t('cleanupOptionTidy')}</span>
              <span className="cleanup-option-desc">{t('cleanupOptionTidyDesc')}</span>
            </span>
          </label>
          <label className={`cleanup-option ${cleanupVariant === 'empty' ? 'selected' : ''}`}>
            <input
              type="radio"
              name="cleanup-variant"
              value="empty"
              checked={cleanupVariant === 'empty'}
              onChange={() => onCleanupVariantChange('empty')}
            />
            <span className="cleanup-option-body">
              <span className="cleanup-option-label">{t('cleanupOptionEmpty')}</span>
              <span className="cleanup-option-desc">{t('cleanupOptionEmptyDesc')}</span>
            </span>
          </label>
        </div>
      </div>
    ) : isLayout ? (
      <div className="style-expander" key="expander-layout">
        <h3 className="style-expander-title">{t('layoutChooseOption')}</h3>
        <div className="cleanup-option-group">
          <label className={`cleanup-option ${layoutVariant === 'flow' ? 'selected' : ''}`}>
            <input
              type="radio"
              name="layout-variant"
              value="flow"
              checked={layoutVariant === 'flow'}
              onChange={() => onLayoutVariantChange('flow')}
            />
            <span className="cleanup-option-body">
              <span className="cleanup-option-label">{t('layoutOptionFlow')}</span>
              <span className="cleanup-option-desc">{t('layoutOptionFlowDesc')}</span>
            </span>
          </label>
          <label className={`cleanup-option ${layoutVariant === 'spacious' ? 'selected' : ''}`}>
            <input
              type="radio"
              name="layout-variant"
              value="spacious"
              checked={layoutVariant === 'spacious'}
              onChange={() => onLayoutVariantChange('spacious')}
            />
            <span className="cleanup-option-body">
              <span className="cleanup-option-label">{t('layoutOptionSpacious')}</span>
              <span className="cleanup-option-desc">{t('layoutOptionSpaciousDesc')}</span>
            </span>
          </label>
        </div>
      </div>
    ) : isReference ? (
      <div className="style-expander" key="expander-reference">
        <h3 className="style-expander-title">{t('uploadReferenceTitle')}</h3>
        <p className="style-expander-hint">{t('uploadReferenceHint')}</p>
        {referenceImage ? (
          <div className="reference-preview">
            <img src={referenceImage.previewUrl} alt={t('altPhoto', { n: 1 })} className="reference-preview-img" />
            <button
              type="button"
              className="reference-preview-remove"
              onClick={() => onReferenceChange(null)}
              aria-label={t('removeReference')}
            >
              <i className="material-symbols-rounded">close</i>
            </button>
          </div>
        ) : (
          <div
            className="reference-upload-area"
            onClick={() => referenceFileInputRef.current?.click()}
          >
            <i className="material-symbols-rounded">add_photo_alternate</i>
            <span>{t('uploadReferenceTitle')}</span>
            <input
              ref={referenceFileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  onReferenceChange({ blob: file, previewUrl: URL.createObjectURL(file) });
                }
                e.target.value = '';
              }}
            />
          </div>
        )}
      </div>
    ) : isCustom ? (
      <div className="style-expander" key={`expander-${selectedStyle}`}>
        <h3 className="style-expander-title">{t('describeCustomStyle')}</h3>
        {customStyleHistory.length > 0 && (
          <select
            className="history-select"
            defaultValue=""
            onChange={(e) => {
              if (e.target.value) {
                onCustomStyleChange(e.target.value.slice(0, MAX_CUSTOM_STYLE));
                logEvent(analytics, 'style_reused');
              }
              e.target.value = '';
            }}
            style={{ marginBottom: '0.75rem' }}
          >
            <option value="">{t('reuseStyle')}</option>
            {customStyleHistory.map((item, i) => (
              <option key={i} value={item}>
                {item.length > 70 ? item.substring(0, 70) + '…' : item}
              </option>
            ))}
          </select>
        )}
        <textarea
          className="custom-style-textarea"
          placeholder={t('customPlaceholder')}
          value={customStyle}
          onChange={(e) => onCustomStyleChange(e.target.value)}
          rows={3}
          maxLength={MAX_CUSTOM_STYLE}
        />
        <div className={`char-counter ${customStyle.length >= MAX_CUSTOM_STYLE ? 'at-limit' : ''}`}>
          {customStyle.length}/{MAX_CUSTOM_STYLE}
        </div>
        <p className="custom-style-public-hint">{t('customStylePublicHint')}</p>
      </div>
    ) : (
      <div className="style-expander" key={`expander-${selectedStyle}`}>
        {showAdditional ? (
          <>
            <h3 className="style-expander-title">
              {t('additionalRequests')}{' '}
              <span className="style-expander-title-optional">{t('optional')}</span>
            </h3>
            <textarea
              className="custom-style-textarea expander-textarea-enter"
              placeholder={t('additionalPlaceholder', { style: styleName(selectedStyle, spaceCategory) || selectedStyle })}
              value={additionalRequest}
              onChange={(e) => onAdditionalRequestChange(e.target.value)}
              rows={2}
              maxLength={MAX_ADDITIONAL_REQUEST}
              autoFocus
            />
            <div className={`char-counter ${additionalRequest.length >= MAX_ADDITIONAL_REQUEST ? 'at-limit' : ''}`}>
              {additionalRequest.length}/{MAX_ADDITIONAL_REQUEST}
            </div>
          </>
        ) : (
          <button
            type="button"
            className="expander-add-btn"
            onClick={() => setShowAdditional(true)}
          >
            {t('addAdditionalRequest')}
          </button>
        )}
      </div>
    )
  );

  return (
    <div>
      {/* Image thumbnails strip */}
      <div className="preview-strip">
        {selectedImages.map((img, i) => (
          <div key={i} className="strip-thumb-wrapper">
            <img src={img.previewUrl} alt={t('altPhoto', { n: i + 1 })} className="strip-thumb" />
            {selectedImages.length > 1 && (
              <span className="strip-label">{t('photoLabel', { n: i + 1 })}</span>
            )}
          </div>
        ))}
      </div>

      {/* Style selection — Room AI 식 이미지 그리드. 카드 = 정사각 톤
         그라데이션 + 짧은 라벨. description 은 결과 화면에 노출. */}
      <h3 className="section-title">{t('chooseStyle')}</h3>
      <div ref={gridRef} className="style-grid">
        {styleKeys.map((key, i) => (
          <Fragment key={key}>
            <button
              type="button"
              className={`style-card style-card-${key} ${selectedStyle === key ? 'selected' : ''}${styleLocked && selectedStyle !== key ? ' locked' : ''}`}
              onClick={() => onStyleSelect(key)}
              disabled={styleLocked && selectedStyle !== key}
              aria-disabled={styleLocked && selectedStyle !== key}
            >
              <span className="style-card-image" aria-hidden="true">
                {key === 'custom' && <i className="material-symbols-rounded">edit</i>}
                {key === 'reference' && <i className="material-symbols-rounded">image</i>}
                {key === 'floorplan' && <i className="material-symbols-rounded">floor</i>}
                {key === 'cleanup' && <i className="material-symbols-rounded">cleaning_services</i>}
                {key === 'layout' && <i className="material-symbols-rounded">view_quilt</i>}
                {/* Real preview thumbnail for non-mode (= actual style) cards.
                    Generated by scripts/build-style-thumbnails.cjs once. */}
                {!MODE_KEYS.includes(key) && (
                  <img
                    src={styleThumb(spaceCategory, key)}
                    onError={styleThumbFallback(spaceCategory, key)}
                    alt=""
                    className="style-card-thumb"
                    loading="lazy"
                  />
                )}
              </span>
              <span className="style-card-label">{styleName(key, spaceCategory)}</span>
            </button>
            {i === lastInRow && expander}
          </Fragment>
        ))}
      </div>

      {/* 커스텀 스타일 공개 정책 1회 공지 — 한 줄짜리 (STYLE_GALLERY.md) */}
      {showShareNotice && (
        <p className="style-share-notice">
          {t('styleShareNotice')}
          {' '}
          <button type="button" className="style-share-notice-link" onClick={() => { dismissShareNotice(); navigate('/account'); }}>
            {t('styleShareNoticeSettings')}
          </button>
          <button type="button" className="style-share-notice-x" onClick={dismissShareNotice} aria-label="dismiss">
            <i className="material-symbols-rounded">close</i>
          </button>
        </p>
      )}

      {/* 커뮤니티 스타일 (STYLE_GALLERY.md) — 인라인은 인기 칩 한 줄로 압축.
         커스텀 카드·통계 전체는 "전체 보기"로 뜨는 StyleGallery 오버레이에. */}
      {trendingChips.length > 0 && (
        <div className="community-styles">
          <div className="community-head">
            <span className="community-label">{t('communityRankTitle')}</span>
            <button type="button" className="community-view-all" onClick={() => setShowGallery(true)}>
              {t('communityViewAll')}
              <i className="material-symbols-rounded">arrow_forward</i>
            </button>
          </div>
          <div className="community-style-row">
            {trendingChips.map((s) => {
              const bare = s.type === 'builtin' ? s.id.replace(/^builtin_/, '') : null;
              const label = s.isCustomAgg ? t('customStylesChip') : styleName(bare, spaceCategory);
              return (
                <button
                  key={s.id}
                  type="button"
                  className="community-mini-card"
                  disabled={styleLocked}
                  onClick={() => (s.isCustomAgg ? setShowGallery(true) : pickBuiltin(bare))}
                >
                  <span className="community-mini-thumb">
                    {s.isCustomAgg
                      ? <i className="material-symbols-rounded">edit</i>
                      : <img src={styleThumb(spaceCategory, bare)} alt="" loading="lazy" onError={styleThumbFallback(spaceCategory, bare)} />}
                  </span>
                  <span className="community-mini-name">{label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {showGallery && (
        <StyleGallery
          stats={communityStats}
          styleKeys={styleKeys}
          spaceCategory={spaceCategory}
          onPickBuiltin={pickBuiltin}
          onPickCustom={pickCustom}
          onClose={() => setShowGallery(false)}
        />
      )}

      {/* Season + time-of-day for outdoor categories (Phase 9-5) */}
      {spaceCategory !== 'interior' && (
        <div className="outdoor-options-row">
          <label className="outdoor-option">
            <span className="outdoor-option-label">{t('seasonLabel')}</span>
            <select
              className="outdoor-option-select"
              value={selectedSeason || 'none'}
              onChange={(e) => onSeasonChange(e.target.value)}
            >
              {SEASON_OPTIONS.map(s => (
                <option key={s} value={s}>{t('seasons.' + s)}</option>
              ))}
            </select>
          </label>
          <label className="outdoor-option">
            <span className="outdoor-option-label">{t('timeOfDayLabel')}</span>
            <select
              className="outdoor-option-select"
              value={selectedTimeOfDay || 'none'}
              onChange={(e) => onTimeOfDayChange(e.target.value)}
            >
              {TIME_OF_DAY_OPTIONS.map(s => (
                <option key={s} value={s}>{t('timesOfDay.' + s)}</option>
              ))}
            </select>
          </label>
        </div>
      )}

      {/* Controls — 데스크탑은 Back / Generate 둘 다 보이고, 모바일은 좌상단
         ← 가 step backward 담당하므로 Back 버튼 숨김. */}
      <div className="controls controls-sticky">
        {/* 사진 여러 장은 그만큼 크레딧이 든다 (사진 1장 = creditPrice). 누른 뒤에
           막히면 사진을 다시 고르러 돌아가야 하므로 여기서 미리 말해 준다.
           creditsAvailable 이 null 이면 Pro 이거나 잔액을 아직 모르는 상태. */}
        {chargedPhotos > 1 && creditsAvailable !== null && (
          <p className={`credit-cost-hint${creditsAvailable < chargedPhotos * creditPrice ? ' is-short' : ''}`}>
            {creditsAvailable < chargedPhotos * creditPrice
              ? t('errCreditsShort', { need: chargedPhotos * creditPrice, have: creditsAvailable })
              : t('creditCostHint', { n: chargedPhotos, c: chargedPhotos * creditPrice, p: creditPrice })}
          </p>
        )}
        <button className="btn btn-secondary step-back-desktop-only" onClick={onBack}>
          {t('back')}
        </button>
        <button
          className="btn btn-primary"
          onClick={onGenerate}
          /* Reference 모드 일 때 사진 + blob 모두 있어야 Generate 진행. prefill
             으로 들어온 reference 의 blob fetch 가 끝나기 전엔 잠깐 disabled. */
          disabled={!selectedStyle || (selectedStyle === 'reference' && !referenceImage?.blob)
            || (isFloorplan && planVariant !== 'clean' && !planStyle)}
        >
          {t('generateDesign')}
        </button>
      </div>

      {pickingRoom && selectedImages[0] && (
        <PlanRoomPicker
          src={selectedImages[0].previewUrl}
          submitLabel={t('planRoomPickDone')}
          onClose={() => setPickingRoom(false)}
          onSubmit={(blob) => {
            if (planRoomMark?.previewUrl) URL.revokeObjectURL(planRoomMark.previewUrl);
            onPlanRoomMarkChange({ blob, previewUrl: URL.createObjectURL(blob) });
            setPickingRoom(false);
            logEvent(analytics, 'plan_room_painted', { surface: 'create' });
          }}
        />
      )}
    </div>
  );
};

// ─── Generate Loading Step ─────────────────────────────────────────────────────
const GenerateStep = ({ error, onRetry, onBack, showRetry = true, pending = null, backLabel = null }) => {
  const { t } = useLocale();
  if (error) {
    return (
      <div className="generate-error">
        <i className="material-symbols-rounded">error_outline</i>
        <h3>{t('generationFailed')}</h3>
        <p>{error}</p>
        <div className="controls">
          {showRetry && (
            <button className="btn btn-primary" onClick={onRetry}>
              <i className="material-symbols-rounded">refresh</i>
              {t('tryAgain')}
            </button>
          )}
          {/* invalidSpace 는 같은 사진으로 재시도해도 같은 결과라 retry 를 숨긴다.
             그러면 남는 버튼이 "뒤로" 하나뿐이라 화면이 막다른 길로 읽혔다 —
             1.1.0 이 2.1(a) 로 거부된 화면이 이것이다. 라벨을 실제로 할 일로
             바꿔서 다음 행동이 보이게 한다. */}
          <button className={`btn ${showRetry ? 'btn-secondary' : 'btn-primary'}`} onClick={onBack}>
            {backLabel ? t(backLabel) : t('back')}
          </button>
        </div>
      </div>
    );
  }
  // Success path — 페이지 본문에 inline progress + percentage. PendingBanner
  // (배너) 는 띄우지 않음. 사용자가 진행 상태를 페이지 안에서 바로 봄.
  const progress = pending?.progress ?? 0;
  return (
    <div className="generate-progress" role="status">
      <h3 className="generate-progress-title">{t('generationInProgress')}</h3>
      <div className="generate-progress-bar">
        <div className="generate-progress-bar-fill" style={{ width: `${progress}%` }} />
      </div>
      <p className="generate-progress-meta">{progress}% · {t('generationKeepBrowsing')}</p>
    </div>
  );
};

// ─── Result Step ───────────────────────────────────────────────────────────────
const ResultStep = ({ result, savedDesignId, user, onReset, onSignIn, onPendingStart, onPendingDone, onPendingError }) => {
  const { t, lang } = useLocale();
  // 게스트에게 남은 무료 횟수를 보여주기 위해서만 쓴다. 예전엔 아무 표시가 없어서
  // 두 번 쓰고 세 번째에 갑자기 막혔다 — 벽이 임의적으로 느껴져 가입으로 이어지지
  // 않았다. 결과를 막 본 직후가 유일한 전환 순간이라 여기서만 말한다.
  const credits = useCredits();
  const creditCfg = useCreditConfig();
  const navigate = useNavigate();
  const [isFavorite, setIsFavorite] = useState(false);
  const [renaming, setRenaming] = useState(false);
  // 모달을 열 때마다 placeholder 를 새로 뽑는다 — 하나로 고정해 두면
  // 다들 그 문구를 그대로 저장한다.
  const [placeholderIdx, setPlaceholderIdx] = useState(randomRenamePlaceholderIndex);
  const [nameInput, setNameInput] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editPhotoIndex, setEditPhotoIndex] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);

  const isOwner = !!(savedDesignId && user && !user.isAnonymous);

  // Signed-in 사용자는 DesignDetail (/designs/{id}) 페이지로 자동 이동 —
  // 거기 owner toolbar (favorite / save / rename / share / post-to-feed /
  // download / delete) 가 풀로 갖춰져 있어 ResultStep 의 줄어든 buttons 보다
  // UX 일관. anonymous 사용자는 sign-in prompt 가 있는 ResultStep 그대로.
  useEffect(() => {
    if (savedDesignId && user && !user.isAnonymous) {
      // justGenerated: DesignDetail 이 여기서 온 것임을 알아야 쓸기 연출을 한다.
      // 로그인 사용자는 ResultStep 을 스쳐 지나가므로 거기 붙은 revealOnMount
      // 는 사실상 익명 사용자 전용이었다 — 생성하고 연출을 못 보던 이유다.
      navigate(`/designs/${savedDesignId}`, { replace: true, state: { justGenerated: true } });
    }
  }, [savedDesignId, user, navigate]);

  if (!result) return null;

  const { generatedImages = [], originalImages = [], originalImage, originalImageUrls = [], generatedImageUrls = [] } = result;
  // Determine how many before/after pairs to show
  // 서버 작업(2.3.0) 결과는 base64 없이 URL 만 온다 — generatedImageUrls 도 센다.
  const photoCount = Math.max(originalImages.length, generatedImages.length, generatedImageUrls.length, 1);

  const handleToggleFavorite = async () => {
    if (!savedDesignId) return;
    const next = await DesignService.toggleFavorite(savedDesignId, isFavorite);
    setIsFavorite(next);
  };

  const handleRename = async () => {
    if (!nameInput.trim() || !savedDesignId) return;
    await DesignService.renameDesign(savedDesignId, nameInput);
    setRenaming(false);
  };

  const handleDelete = async () => {
    if (!savedDesignId) return;
    setDeleting(true);
    try {
      await DesignService.deleteDesign({ id: savedDesignId, originalImageUrls, generatedImageUrls });
      onReset();
    } catch {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const shareUrl = savedDesignId
    ? `${publicOrigin()}/s/${savedDesignId}`
    : '';

  const handleShare = async () => {
    if (!shareUrl) return;
    // 공유하면 곧 공개다. DesignDetail 의 share 는 원래 이걸 했는데 여기만
    // 빠져 있어서, 생성 직후 공유한 링크와 보관함에서 공유한 링크가 서로
    // 다르게 동작했다. 링크를 넘긴다는 건 보라는 뜻이다.
    try { await DesignService.setPublic(savedDesignId, true); }
    catch (e) { console.warn('setPublic on share:', e?.message); }
    const shared = await shareLink({ url: shareUrl });
    if (!shared) {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  // The EditRegionModal expects a design-shaped object. Build one from the in-memory result.
  const editDesignShape = savedDesignId ? {
    id: savedDesignId,
    style: result.style,
    customStyleInput: result.customStyleInput,
    additionalRequest: result.additionalRequest,
    budget: result.budget,
    mode: result.mode,
    lang: result.lang,
    spaceAnalysis: result.spaceAnalysis,
    styleDescription: result.styleDescription,
    colorPalette: result.colorPalette,
    furnitureRecommendations: result.furnitureRecommendations,
    decorativeElements: result.decorativeElements,
    lightingPlan: result.lightingPlan,
    estimatedCost: result.estimatedCost,
    tips: result.tips,
    originalImageUrls,
    generatedImageUrls,
  } : null;

  return (
    <div>
      {/* Owner action toolbar (mirrors DesignDetail) */}
      {isOwner && (
        <div className="detail-topbar-row" style={{ justifyContent: 'flex-end' }}>
          <div className="detail-actions">
            <button
              className={`detail-icon-btn ${isFavorite ? 'fav-active' : ''}`}
              onClick={handleToggleFavorite}
              title={isFavorite ? t('unfavorite') : t('favorite')}
              aria-label={isFavorite ? t('unfavorite') : t('favorite')}
            >
              <i className="material-symbols-rounded">favorite</i>
            </button>
            <button
              className="detail-icon-btn"
              onClick={() => { setNameInput(''); setPlaceholderIdx(randomRenamePlaceholderIndex()); setRenaming(true); }}
              title={t('rename')}
              aria-label={t('rename')}
            >
              <i className="material-symbols-rounded">edit</i>
            </button>
            <button
              className="detail-icon-btn"
              onClick={handleShare}
              title={linkCopied ? t('copied') : t('share')}
              aria-label={t('share')}
            >
              <i className="material-symbols-rounded">{linkCopied ? 'check' : 'ios_share'}</i>
            </button>
            <button
              className="detail-icon-btn danger-btn"
              onClick={() => setConfirmDelete(true)}
              title={t('delete')}
              aria-label={t('delete')}
            >
              <i className="material-symbols-rounded">delete</i>
            </button>
          </div>
        </div>
      )}

      {/* Phase 9-6: floor plan auto-detect notice. Heads off the "why is my
         whole-floor plan rendered as a single living room?" reaction. */}
      {result.detectedInputType === 'floorplan' && !result.outputType && (
        <div className="floorplan-detected-banner" role="status">
          <i className="material-symbols-rounded">info</i>
          <span>
            {result.floorplanRoomFocus
              ? t('floorplanDetectedBannerWithRoom', { room: result.floorplanRoomFocus })
              : t('floorplanDetectedBanner')}
          </span>
        </div>
      )}

      {/* Before / After for each photo */}
      {Array.from({ length: photoCount }, (_, i) => {
        const beforeSrc = originalImages[i] || originalImage;
        const genImg = generatedImages[i];
        const afterSrc = genImg?.data ? `data:${genImg.mimeType || 'image/jpeg'};base64,${genImg.data}` : (generatedImageUrls[i] || null);

        return (
          <div key={i} className="result-photo-pair">
            {photoCount > 1 && (
              <h3 className="result-photo-label">{t('photoLabel', { n: i + 1 })}</h3>
            )}
            {beforeSrc && afterSrc ? (
              <>
                {result.outputType === 'plan_room' ? (
                  <PlanRoomResult src={afterSrc} planSrc={beforeSrc} alt={t('altGenerated', { n: i + 1 })} />
                ) : (
                  <BeforeAfterSlider
                    beforeSrc={beforeSrc}
                    afterSrc={afterSrc}
                    beforeLabel={planSliderLabels(t, result.outputType).before}
                    afterLabel={planSliderLabels(t, result.outputType).after}
                    alt={t('altPhoto', { n: i + 1 })}
                    revealOnMount
                  />
                )}
                {isOwner && (
                  <div className="controls" style={{ justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                    <button
                      className="btn btn-secondary edit-region-btn"
                      onClick={() => setEditPhotoIndex(i)}
                    >
                      <i className="material-symbols-rounded">auto_fix_high</i>
                      {t('editRegion')}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="image-comparison">
                <div className="image-container">
                  <h3>{t('original')}</h3>
                  <img src={beforeSrc} alt={t('altOriginal', { n: i + 1 })} className="preview-image" />
                </div>
                <div className="image-container">
                  <h3>{t('aiDesign')}</h3>
                  <div className="image-error">
                    <p>{t('imageGenFailed')}</p>
                    <p>{t('seeAnalysis')}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Lang mismatch notice */}
      {result.lang && result.lang !== lang && (
        <div className="lang-notice">
          <i className="material-symbols-rounded">info</i>
          <span>{t('langMismatchNotice', { lang: LANG_LABELS[result.lang] || result.lang })}</span>
        </div>
      )}

      {/* Shared design analysis */}
      <div className="preview-container">
        <h3>{t('analysisTitle')}</h3>

        <div>
          <h4>{t('spaceAnalysis')}</h4>
          <div className="md-content">
            <ReactMarkdown>{result.spaceAnalysis}</ReactMarkdown>
          </div>
        </div>

        <div>
          <h4>{t('styleDescription')}</h4>
          <div className="md-content">
            <ReactMarkdown>{result.styleDescription}</ReactMarkdown>
          </div>
        </div>

        {result.outputType !== 'plan_clean' && (<>
        <div>
          <h4>{t('colorPalette')}</h4>
          <div className="color-palette">
            {result.colorPalette?.map((color, i) => (
              <div key={i} className="color-swatch-wrapper">
                <div
                  className={`color-swatch ${color === 'ERROR' ? 'color-error' : ''}`}
                  style={{ backgroundColor: color === 'ERROR' ? 'transparent' : color }}
                  title={color}
                />
                {color !== 'ERROR' && <span className="color-swatch-code">{color}</span>}
              </div>
            ))}
          </div>
        </div>

        {result.furnitureRecommendations && (
          <div>
            <h4>{t('furnitureRec')}</h4>
            <div className="recommendations-list md-content">
              <ul>
                {result.furnitureRecommendations.map((item, i) => {
                  const shopUrl = buildShoppingUrl(item.searchQuery || item.item, lang);
                  return (
                    <li key={i}>
                      <strong>{item.item}</strong>
                      {shopUrl && (
                        <a
                          className="shop-link"
                          href={shopUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={t('shopFor', { item: item.item })}
                          onClick={() => logEvent(analytics, 'furniture_shop_clicked', { provider: shoppingProvider(lang) })}
                        >
                          <i className="material-symbols-rounded">shopping_bag</i>
                          <span>{t('shopShort')}</span>
                        </a>
                      )}
                      : <ReactMarkdown components={{ p: ({children}) => <>{children}</> }}>{item.description}</ReactMarkdown>
                      {item.placement && <> (<ReactMarkdown components={{ p: ({children}) => <>{children}</> }}>{item.placement}</ReactMarkdown>)</>}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        )}

        {result.lightingPlan && (
          <div>
            <h4>{t('lightingPlan')}</h4>
            <div className="md-content">
              <ReactMarkdown>{result.lightingPlan}</ReactMarkdown>
            </div>
          </div>
        )}

        {result.tips && (
          <div>
            <h4>{t('designTips')}</h4>
            <div className="recommendations-list md-content">
              <ul>
                {result.tips.map((tip, i) => <li key={i}><ReactMarkdown>{tip}</ReactMarkdown></li>)}
              </ul>
            </div>
          </div>
        )}
        </>)}
      </div>

      <div className="controls">
        <button className="btn btn-primary" onClick={onReset}>
          {t('startNew')}
        </button>
        {isOwner && (
          <button className="btn btn-secondary" onClick={() => navigate('/designs')}>
            <i className="material-symbols-rounded">grid_view</i>
            {t('viewMyDesigns')}
          </button>
        )}
      </div>
      {onSignIn && (
        <div className="save-prompt">
          <i className="material-symbols-rounded">cloud_off</i>
          <p>{t('savePromptText')}</p>
          {!credits.loading && credits.credits !== null && (
            <p className="save-prompt-quota">
              {credits.credits > 0
                ? t('guestCreditsLeft', { n: credits.guestDesigns ?? credits.credits, total: creditCfg.signupBonus })
                : t('guestCreditsGone', { total: creditCfg.signupBonus })}
            </p>
          )}
          <button className="btn btn-primary" onClick={onSignIn}>
            <i className="material-symbols-rounded">login</i>
            {t('signInGoogle')}
          </button>
        </div>
      )}

      {/* Rename modal */}
      {renaming && (
        <div className="modal-overlay" onClick={() => setRenaming(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <h3>{t('renameTitle')}</h3>
            <input
              className="rename-input"
              value={nameInput}
              onChange={e => setNameInput(e.target.value)}
              placeholder={t(`renamePlaceholders.${placeholderIdx}`)}
              autoFocus
              onKeyDown={e => { if (e.key === 'Enter') handleRename(); if (e.key === 'Escape') setRenaming(false); }}
            />
            <div className="controls" style={{ marginTop: '1rem' }}>
              <button className="btn btn-primary" onClick={handleRename}>{t('save')}</button>
              <button className="btn btn-secondary" onClick={() => setRenaming(false)}>{t('cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {confirmDelete && (
        <div className="modal-overlay" onClick={() => setConfirmDelete(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <h3>{t('deleteTitle')}</h3>
            <p style={{ color: 'var(--text-secondary)', marginTop: '0.5rem' }}>{t('deleteDesc')}</p>
            <div className="controls" style={{ marginTop: '1.5rem' }}>
              <button
                className="btn btn-primary"
                style={{ background: 'var(--error)', borderColor: 'var(--error)' }}
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? t('deleting') : t('delete')}
              </button>
              <button className="btn btn-secondary" onClick={() => setConfirmDelete(false)}>{t('cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit region modal */}
      {editPhotoIndex !== null && editDesignShape && (
        <EditRegionModal
          design={editDesignShape}
          photoIndex={editPhotoIndex}
          user={user}
          onClose={() => setEditPhotoIndex(null)}
          onPendingStart={onPendingStart}
          onPendingDone={onPendingDone}
          onPendingError={onPendingError}
        />
      )}
    </div>
  );
};

// ─── Community Feed ────────────────────────────────────────────────────────────
// sessionStorage cache key — feed designs are reused within the tab session so
// re-visiting the home page doesn't trigger a full Firestore fetch + image
// reload flash. Only the default view (no filters) is cached; filtered views
// hit the server fresh.
const FEED_CACHE_KEY = 'voda_feed_cache_v1';
// 24시간 TTL — sessionStorage (탭 닫으면 휘발) 대신 localStorage (앱 콜드 스타트
// 사이에도 영속) 로 변경. 사용자가 앱 다시 열면 지난 세션의 feed 가 stale 한
// 상태로라도 *즉시* 렌더되고, 백그라운드로 prewarm 이 fresh 데이터로 갱신.
// "stale-while-revalidate" 패턴 — splash 끝나기 전에 prewarm 끝낼 수 있는 시간이
// 부족해서 (Capacitor cold start ~2.5s + 네트워크 ~1-2s) 영속 캐시가 첫 클릭
// instant 의 유일한 답.
const FEED_CACHE_TTL = 24 * 60 * 60 * 1000;

function readFeedCache() {
  try {
    const raw = localStorage.getItem(FEED_CACHE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || typeof data !== 'object') return null;
    if (Date.now() - (data.savedAt || 0) > FEED_CACHE_TTL) return null;
    return data;
  } catch {
    return null;
  }
}

function writeFeedCache(designs) {
  try {
    localStorage.setItem(FEED_CACHE_KEY, JSON.stringify({
      savedAt: Date.now(),
      designs,
    }));
  } catch {
    // Ignore quota errors — localStorage cache is best-effort.
  }
}

function CommunityFeed({ user, openSignIn }) {
  const { t, styleName } = useLocale();
  // Hydrate from cache synchronously so the very first paint already has
  // the previously-seen designs — avoids the empty → spinner → designs flash.
  const cached = readFeedCache();
  const [designs, setDesigns] = useState(cached?.designs || []);
  const [loading, setLoading] = useState(!cached);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  // URL search param `?style=X&category=Y` 으로 외부 deep-link (예: 디자인
  // 디테일 페이지의 스타일 태그 클릭) 받음. 그 외엔 default null.
  const [searchParamsForFeed] = useSearchParams();
  const initialStyle = searchParamsForFeed.get('style') || null;
  const initialCategory = searchParamsForFeed.get('category') || null;
  const [sortBy, setSortBy] = useState('latest');
  const [styleFilter, setStyleFilter] = useState(initialStyle);
  const [tab, setTab] = useState('all'); // 'all' | 'following'
  const [spaceCategory, setSpaceCategoryFilter] = useState(initialCategory); // null | 'interior' | 'exterior' | 'garden'
  const [followingIds, setFollowingIds] = useState(null); // null = unloaded
  const [authorMap, setAuthorMap] = useState(() => new Map());
  const isLoggedIn = user && !user.isAnonymous;
  // 차단한 사용자의 디자인은 피드에서 숨김 (Apple Guideline 1.2). 차단/해제
  // 즉시 반영 — 페이지 새로고침 불필요.
  const blockedUids = useBlockedUids(user);
  const visibleDesigns = blockedUids.size > 0
    ? designs.filter(d => !blockedUids.has(d.userId))
    : designs;

  // URL search param 변경 watch — 사용자가 디자인 detail 의 style 태그 클릭
  // (navigate `/feed?style=X`) 후 또 다른 태그 클릭 시도 시에도 filter 따라감.
  useEffect(() => {
    const style = searchParamsForFeed.get('style') || null;
    const category = searchParamsForFeed.get('category') || null;
    setStyleFilter(prev => (prev === style ? prev : style));
    setSpaceCategoryFilter(prev => (prev === category ? prev : category));
  }, [searchParamsForFeed]);

  // Whenever the design list changes, fetch any author profiles we don't yet
  // have cached. FeedCard renders the author chip from `authorMap`.
  useEffect(() => {
    const missing = designs
      .map(d => d.userId)
      .filter(uid => uid && uid !== 'anonymous' && !authorMap.has(uid));
    if (missing.length === 0) return;
    let cancelled = false;
    ProfileService.getProfilesByUids(missing).then(map => {
      if (cancelled || map.size === 0) return;
      setAuthorMap(prev => {
        const next = new Map(prev);
        map.forEach((p, uid) => next.set(uid, p));
        return next;
      });
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [designs]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadFeed = useCallback(async (reset = false, sort = sortBy, style = styleFilter, currentTab = tab, category = spaceCategory) => {
    // Don't blank the grid on a background refresh when we already have
    // cached designs — let them stay visible until the new fetch lands.
    if (reset && designs.length === 0) setLoading(true);
    if (!reset) setLoadingMore(true);
    try {
      let userIdsArg = undefined;
      if (currentTab === 'following') {
        if (!isLoggedIn) {
          setDesigns([]); setHasMore(false); setLastDoc(null);
          return;
        }
        let ids = followingIds;
        if (ids === null) {
          ids = await FollowService.getFollowingIds(user.uid);
          setFollowingIds(ids);
        }
        userIdsArg = ids;
      }
      const result = await DesignService.getFeedDesigns({
        lastDoc: reset ? null : lastDoc,
        sortBy: sort,
        styleFilter: style,
        userIds: userIdsArg,
        spaceCategory: category,
      });
      setDesigns(prev => reset ? result.designs : [...prev, ...result.designs]);
      setLastDoc(result.lastVisible);
      setHasMore(result.hasMore);

      // Cache only the default view — filtered views are easy to refetch
      // and caching every combination would bloat sessionStorage.
      if (reset && currentTab === 'all' && !style && !category && sort === 'latest') {
        writeFeedCache(result.designs);
      }
    } catch (e) {
      console.error('Feed load error:', e);
    } finally {
      if (reset) setLoading(false); else setLoadingMore(false);
    }
  }, [lastDoc, sortBy, styleFilter, tab, spaceCategory, isLoggedIn, user, followingIds, designs.length]);

  useEffect(() => {
    loadFeed(true, sortBy, styleFilter, tab, spaceCategory);
  }, [sortBy, styleFilter, tab, spaceCategory]); // eslint-disable-line

  // Whenever the space category changes, reset the style filter — the
  // available styles are different per category.
  const handleSpaceCategoryFilterChange = (cat) => {
    setSpaceCategoryFilter(cat);
    setStyleFilter(null);
  };

  const handleLike = async (designId, currentlyLiked) => {
    if (!user || user.isAnonymous) {
      openSignIn?.();
      return;
    }
    setDesigns(prev => prev.map(d => {
      if (d.id !== designId) return d;
      const newLikedBy = currentlyLiked
        ? (d.likedBy || []).filter(uid => uid !== user.uid)
        : [...(d.likedBy || []), user.uid];
      return { ...d, likedBy: newLikedBy, likeCount: Math.max(0, (d.likeCount || 0) + (currentlyLiked ? -1 : 1)) };
    }));
    try {
      await DesignService.toggleLike(designId, user.uid, currentlyLiked);
    } catch {
      setDesigns(prev => prev.map(d => {
        if (d.id !== designId) return d;
        const revert = currentlyLiked
          ? [...(d.likedBy || []), user.uid]
          : (d.likedBy || []).filter(uid => uid !== user.uid);
        return { ...d, likedBy: revert, likeCount: Math.max(0, (d.likeCount || 0) + (currentlyLiked ? 1 : -1)) };
      }));
    }
  };

  // Pick the right style set + i18n path for the current category filter.
  // styleCatalog() 를 거쳐야 시즌 지난 스타일(크리스마스 등)이 빠진다 —
  // AIService.interiorStyles 를 직접 읽으면 9월에 크리스마스 타일이 뜬다.
  useSeasonConfig();
  const styleCatalogForFilter = AIService.styleCatalog(spaceCategory);
  const styleI18nPathForFilter = spaceCategory === 'exterior' ? 'exteriorStyles'
    : spaceCategory === 'garden' ? 'gardenStyles'
    : 'styles';
  const allStyles = Object.keys(styleCatalogForFilter).filter(k => k !== 'custom');

  return (
    <div className="community-feed">
      <div className="feed-header">
        <h2 className="feed-title">{t('navFeed')}</h2>
      </div>

      {/* 모바일 한정 sticky+auto-hide wrapper. 데스크톱은 inline 그대로
         (CSS 가 모바일에서만 sticky 적용). 스크롤 다운하면 body.scroll-down
         감지 → 위로 슥 사라짐, 업 시 부드럽게 내려옴. */}
      <div className="feed-sticky-mobile">
      {/* All / Following 좌측 + Latest / Popular 우측 — 한 줄에 묶어서
         공간 효율. 너비 좁으면 wrap. */}
      <div className="feed-tabs-row">
        <div className="feed-tabs">
          <button
            className={`feed-tab ${tab === 'all' ? 'active' : ''}`}
            onClick={() => setTab('all')}
          >
            {t('feedFilterAll')}
          </button>
          <button
            className={`feed-tab ${tab === 'following' ? 'active' : ''}`}
            onClick={() => {
              if (!isLoggedIn) { openSignIn?.(); return; }
              setTab('following');
              setFollowingIds(null); // refresh on next load
            }}
          >
            {t('followingTab')}
          </button>
        </div>
        <div className="feed-sort-btns">
          <button className={`feed-sort-btn ${sortBy === 'latest' ? 'active' : ''}`} onClick={() => setSortBy('latest')}>
            {t('feedSortLatest')}
          </button>
          <button className={`feed-sort-btn ${sortBy === 'popular' ? 'active' : ''}`} onClick={() => setSortBy('popular')}>
            {t('feedSortPopular')}
          </button>
        </div>
      </div>

      {/* Space category filter (Phase 9-5) — three buttons + 「All」 */}
      <div className="feed-category-filter">
        <button
          className={`feed-filter-btn ${!spaceCategory ? 'active' : ''}`}
          onClick={() => handleSpaceCategoryFilterChange(null)}
        >
          {t('feedFilterAll')}
        </button>
        {SPACE_CATEGORIES.map(cat => (
          <button
            key={cat}
            className={`feed-filter-btn ${spaceCategory === cat ? 'active' : ''}`}
            onClick={() => handleSpaceCategoryFilterChange(cat)}
          >
            {t('spaceCategories.' + cat)}
          </button>
        ))}
      </div>

      {/* Style filter — only when a specific category is selected. The「전체」
         category covers ~23 styles across all categories, too many to scroll. */}
      {spaceCategory && (
        <div className="feed-style-filter">
          <button className={`feed-filter-btn ${!styleFilter ? 'active' : ''}`} onClick={() => setStyleFilter(null)}>
            {t('feedFilterAll')}
          </button>
          {allStyles.map(key => (
            <button
              key={key}
              className={`feed-filter-btn ${styleFilter === key ? 'active' : ''}`}
              onClick={() => setStyleFilter(key)}
            >
              {styleName(key, spaceCategory)}
            </button>
          ))}
          {/* Custom style chip — interior only */}
          {spaceCategory === 'interior' && (
            <button
              className={`feed-filter-btn ${styleFilter === 'custom' ? 'active' : ''}`}
              onClick={() => setStyleFilter('custom')}
            >
              {styleName('custom')}
            </button>
          )}
        </div>
      )}
      </div>{/* /.feed-sticky-mobile */}

      {loading ? (
        <div className="loading" style={{ minHeight: '200px' }}><div className="spinner"></div></div>
      ) : visibleDesigns.length === 0 ? (
        <p className="feed-empty">
          {tab === 'following' ? t('followingEmpty') : t('feedEmpty')}
        </p>
      ) : (
        <>
          <div className="feed-grid">
            {visibleDesigns.map(d => (
              <FeedCard
                key={d.id}
                design={d}
                user={user}
                author={authorMap.get(d.userId)}
                onLike={handleLike}
                onSignInRequest={openSignIn}
              />
            ))}
          </div>
          {hasMore && (
            <div className="controls" style={{ justifyContent: 'center', marginTop: '1.5rem' }}>
              <button className="btn btn-secondary" onClick={() => loadFeed(false)} disabled={loadingMore}>
                {t('loadMore')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// 마케팅 landing 위에 Welcome 모달이 뜨면 안 됨 — landing 자체가 sign-in 유도
// 역할을 함. 정책/법적 페이지 (/privacy, /terms, /support, /delete-account) 도
// 제외 — Google Play / App Store review 가 URL 직접 접근했을 때 로그인 벽이
// 있으면 invalid privacy policy 로 판정. 이 페이지들은 익명으로 즉시 읽혀야 함.
const WELCOME_BLOCKED_PATHS = new Set([
  '/landing',
  '/privacy',
  '/terms',
  '/support',
  '/delete-account',
]);

// 링크를 받고 들어오는 자리 — 디자인·공유·프로필·컬렉션.
//
// **웹에서는** 여기에 Welcome 을 띄우지 않는다. "보는 건 웹에서, 하는 건
// 앱에서" 인데 보러 온 사람 앞에 로그인 벽을 세우면 보는 것부터 막힌다.
// 게다가 웹에서는 아래처럼 "계정 없이 계속" 을 빼서 닫을 방법이 없으므로,
// 그냥 벽이 아니라 **못 빠져나오는 벽**이 된다.
//
// 네이티브 앱에서는 그대로 띄운다 — 거기선 게스트로 빠져나올 수 있고,
// 앱을 처음 연 사람에게 가입 제안을 하는 건 정상이다.
const VIEW_ONLY_PATTERNS = [
  /^\/designs\/[^/]+$/,
  /^\/s\/[^/]+$/,
  /^\/u\/[^/]+$/,
  /^\/c\/[^/]+$/,
];
function WelcomeGate({ children }) {
  const { pathname } = useLocation();
  if (WELCOME_BLOCKED_PATHS.has(pathname) || isLandingPath(pathname)) return null;
  if (!isNativeApp() && VIEW_ONLY_PATTERNS.some((re) => re.test(pathname))) return null;
  return children;
}

// '/' route —
//  - 마케팅 host (archelier.co) 만: /landing 으로. 다운로드 유도가 첫 인상.
//  - 그 외 web host (voda-7647c.web.app, localhost): 모바일 /create,
//    데스크탑 HomePage. 개발/스테이징 환경에서 landing 우회.
//  - 네이티브 앱 (Capacitor): 모바일 /create, 데스크탑(미사용) HomePage.
//  /s/:id 등 deep link 는 그대로 — 이 라우트는 '/' 만 처리.
function HomeRoute(props) {
  const { user } = props;
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia('(max-width: 768px)');
    const onChange = () => setIsMobile(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  const isMarketingHost = typeof window !== 'undefined'
    && /(^|\.)archelier\.co$/i.test(window.location.hostname);

  // ?ref= 로 들어온 사람 (main.jsx 가 이미 localStorage 로 옮겨 둠).
  // 로그인돼 있으면 /invite 로 보낸다 — 코드가 폼에 미리 채워져 한 번에
  // 회수된다. 자동 회수는 "로그인 직후" 한 번만 돌아서(auth-service.js 의
  // initializeCredits), 이미 로그인한 세션에서는 아무도 그 코드를 안 쓴다.
  // 로그아웃·게스트는 /landing 유지 — /invite 는 로그인 벽이라 초대 안내도
  // 설치 버튼도 없어서 처음 온 사람에게 더 나쁘다.
  // user 가 undefined 면 auth 미해결 — ref 가 있을 때만 잠깐 기다린다
  // (없을 때까지 기다리면 마케팅 페이지 첫 페인트가 늦어진다).
  if (ReferralService.getPendingReferral()) {
    if (user === undefined) return null;
    if (user && !user.isAnonymous) return <Navigate to="/invite" replace />;
  }

  if (!isNativeApp() && isMarketingHost) return <Navigate to="/landing" replace />;
  if (isMobile) return <Navigate to="/create" replace />;
  return <HomePage {...props} />;
}

// ─── Home Page (upload flow) ───────────────────────────────────────────────────
function HomePage({ user, onSignInRequest, onOutOfCredits, onCreditsClick, pendingActive, pending, onGenerationStart, onGenerationDone, onGenerationError, onDismissPending }) {
  const { t, lang, locale, styleName } = useLocale();
  const credits = useCredits();
  const creditCfg = useCreditConfig();
  const creditPrice = creditCfg.price.generate;
  // Pro (기존 결제자) 는 무제한이라 크레딧 계산에서 빠지고, 스냅샷이 아직 안 온
  // 동안 (loading / null) 은 아무 것도 막지 않는다 — 잔액을 모르는 상태에서
  // 막으면 멀쩡한 사용자를 세우게 된다.
  const creditsKnown = credits.plan !== 'pro' && !credits.loading && credits.credits !== null;
  const location = useLocation();
  const prefill = location.state || {};
  const [searchParams, setSearchParams] = useSearchParams();

  // currentStep 이 'upload' / 'style' 인 동안 URL search param 'step' 도 sync.
  // 좌상단 ← (navigate(-1)) 가 자동으로 step backward 처리되도록.
  // 'generate' / 'result' 는 URL 안 건드림 (일시 state).
  const [currentStep, setCurrentStepRaw] = useState(searchParams.get('step') === 'style' ? 'style' : 'upload');
  const setCurrentStep = useCallback((next) => {
    setCurrentStepRaw(next);
    if (next === 'upload') {
      setSearchParams({}, { replace: false });
    } else if (next === 'style') {
      setSearchParams({ step: 'style' }, { replace: false });
    }
    // generate / result 은 URL 변경 안 함 — popstate 시 일관성 유지
  }, [setSearchParams]);

  // step 이 바뀌면 맨 위로. 라우트는 계속 /create 라서 App 의 ScrollToTop
  // (pathname 변경에만 반응) 이 안 걸린다 — step 2 에서 스타일을 찾느라 한참
  // 내려간 상태 그대로 결과 화면이 떠서, before/after 쓸기 연출이 화면 밖에서
  // 끝나 "연출이 안 돈다" 로 보였다. 각 step 은 사실상 다른 화면이다.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  // URL ↔ state sync: ← 또는 forward 등 외부에서 URL 변경 시 state 따라감.
  useEffect(() => {
    const urlStep = searchParams.get('step');
    if (urlStep === 'style' && currentStep === 'upload') setCurrentStepRaw('style');
    else if (!urlStep && currentStep === 'style') setCurrentStepRaw('upload');
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps
  const [selectedImages, setSelectedImages] = useState([]);
  // 사진은 메모리에만 있다 — 새로고침하면 ?step=style 만 남아 사진 없는 스타일 화면이 떴고,
  // 평면도 방 칠하기·만들기 버튼이 아무 반응 없이 멈췄다. 사진이 없으면 올리기로 돌린다.
  useEffect(() => {
    if (currentStep === 'style' && !selectedImages.length) {
      setCurrentStepRaw('upload');
      setSearchParams({}, { replace: true });
    }
  }, [currentStep, selectedImages.length]); // eslint-disable-line react-hooks/exhaustive-deps
  const [selectedSpaceCategory, setSelectedSpaceCategory] = useState(prefill.spaceCategory || 'interior');
  const [selectedSeason, setSelectedSeason] = useState(prefill.exteriorOptions?.season || 'none');
  const [selectedTimeOfDay, setSelectedTimeOfDay] = useState(prefill.exteriorOptions?.timeOfDay || 'none');
  // Reference Style — 사용자가 「Reference Photo」 선택 시 업로드한 1장.
  // { blob, previewUrl } 또는 null. style !== 'reference' 면 무시됨.
  const [referenceImage, setReferenceImage] = useState(null);

  // prefill 으로 referenceImageUrl 이 들어오면 blob 으로 변환해서 state 채움
  // (use-this-style 으로 들어와 같은 reference 자동 채워지는 흐름).
  useEffect(() => {
    if (!prefill.referenceImageUrl || prefill.style !== 'reference') return;
    // 일단 URL 만으로 preview 표시 — fetch 실패해도 사용자에게 reference 보임.
    // blob 은 generation 위해 백그라운드 fetch.
    setReferenceImage({ blob: null, previewUrl: prefill.referenceImageUrl });
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(prefill.referenceImageUrl);
        if (!res.ok) throw new Error(`fetch ${res.status}`);
        const blob = await res.blob();
        if (!cancelled) {
          setReferenceImage(prev => prev ? { ...prev, blob } : { blob, previewUrl: prefill.referenceImageUrl });
        }
      } catch (err) {
        console.warn('[reference prefill] fetch failed:', err.message, prefill.referenceImageUrl);
      }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Cleanup 모드 sub-option — 'tidy' (잡동사니 정리) 또는 'empty' (빈 방).
  const [cleanupVariant, setCleanupVariant] = useState('tidy');
  const [layoutVariant, setLayoutVariant] = useState('flow');
  // 평면도 모드 (2.4.0) — '3d' | 'room' | 'clean', 정리는 'line' | 'color'.
  // 다시 만들기(planPrefill)로 들어오면 그때 고른 값으로 시작한다.
  const [planVariant, setPlanVariant] = useState(prefill.planVariant || '3d');
  const [planStyle, setPlanStyle] = useState(prefill.planStyle || 'modern');
  const [planCleanKind, setPlanCleanKind] = useState(prefill.planCleanKind || 'line');
  const [planRoomMark, setPlanRoomMark] = useState(null); // { blob, previewUrl } — 칠한 평면도 사본
  // 평면도(첫 장)를 바꾸면 그 위에 칠한 방은 무효다.
  const firstPreview = selectedImages[0]?.previewUrl;
  useEffect(() => { setPlanRoomMark(null); }, [firstPreview]);
  const [selectedBudget, setSelectedBudget] = useState(prefill.budget || 'mid');
  const [selectedStyle, setSelectedStyle] = useState(prefill.style || '');
  const [customStyle, setCustomStyle] = useState(prefill.customStyle || '');
  const [additionalRequest, setAdditionalRequest] = useState(prefill.additionalRequest || '');
  const [customStyleHistory, setCustomStyleHistory] = useState(() => AIService.getCustomStyleHistory());
  const [sourceName, setSourceName] = useState(prefill.sourceName || null);
  // 갤러리 탭에서 스타일을 골라 들어오면 첫 화면(사진 올리기)엔 스타일 칸이 없어서 무엇이
  // 골라졌는지 안 보였다 — 한 줄로 알린다. 다른 스타일로 바꾸면 사라진다.
  const [galleryPickShown, setGalleryPickShown] = useState(!!prefill.fromGallery);
  const [designResult, setDesignResult] = useState(null);
  const [savedDesignId, setSavedDesignId] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [error, setError] = useState(null);
  const [generationError, setGenerationError] = useState(null);
  const [invalidSpaceError, setInvalidSpaceError] = useState(false);

  // If the user stays on the home screen until ResultStep mounts, suppress
  // the floating "Open" banner — the result is already on-screen, the
  // banner would be redundant. The banner is for users who navigated away.
  useEffect(() => {
    if (currentStep === 'result') onDismissPending?.();
  }, [currentStep, onDismissPending]);

  const processAndAddImage = useCallback(async (file) => {
    if (selectedImages.length >= MAX_PHOTOS) return;
    try {
      setError(null);
      CameraService.validateImageFile(file);
      const compressed = await CameraService.compressImage(file);
      const previewUrl = URL.createObjectURL(compressed);
      setSelectedImages(prev => [...prev, { blob: compressed, previewUrl }]);
    } catch (err) {
      setError(err.message === 'FILE_TYPE' ? t('errFileType')
        : err.message === 'FILE_TOO_LARGE' ? t('errFileTooLarge')
        : t('errPhotoRead'));
    }
  }, [selectedImages.length, t]);

  const handleCameraCapture = useCallback(async (blob) => {
    if (selectedImages.length >= MAX_PHOTOS) { setShowCamera(false); return; }
    try {
      const compressed = await CameraService.compressBlob(blob);
      const previewUrl = URL.createObjectURL(compressed);
      setSelectedImages(prev => [...prev, { blob: compressed, previewUrl }]);
    } catch (err) {
      console.warn('camera capture:', err?.message);
      setError(t('errPhotoRead'));
    }
    setShowCamera(false);
  }, [selectedImages.length, t]);

  const removeImage = useCallback((index) => {
    setSelectedImages(prev => {
      const next = [...prev];
      URL.revokeObjectURL(next[index].previewUrl);
      next.splice(index, 1);
      return next;
    });
  }, []);

  const getErrorMessage = (err) => {
    if (!navigator.onLine) return t('errNoInternet');
    if (err?.code === 'INSUFFICIENT_CREDITS' || err?.status === 402) {
      // 수익화 분기점 지표 — 크레딧이 벽이 된 순간. /admin 과 MONETIZATION.md 참고.
      logEvent(analytics, 'credit_exhausted', { surface: 'generate' });
      // 사전 검사를 뚫고 온 경우의 안전망. 서버가 숫자를 보내줬고 실제로 잔액이
      // 남아 있으면 "모두 소진" 이 아니라 부족 문구를 쓴다 — 사진을 줄이면 되는
      // 상황에서 "내일 오세요" 는 틀린 안내다.
      if (err.required > 0 && err.available > 0) {
        return t('errCreditsShort', { need: err.required, have: err.available });
      }
      // 사전 검사와 같은 벽 — 크레딧 모달(앱이면 팩 구매)을 같이 띄운다.
      onOutOfCredits?.();
      return credits.isGuest ? t('errInsufficientCredits')
        : t('errOutOfCreditsToday', { n: Math.floor(creditCfg.daily / creditPrice) });
    }
    if (err?.code === 'INVALID_CUSTOM_COMMAND') return t('errBlockedCustomCommand');
    if (err?.code === 'functions/deadline-exceeded') return t('errTimeout');
    if (err?.code === 'functions/resource-exhausted') return t('errTooMany');
    if (err?.message?.includes('Failed to fetch') || err?.message?.includes('NetworkError')) {
      return t('errNetwork');
    }
    return t('errAI');
  };

  const generateDesign = async () => {
    if (!selectedImages.length || !selectedStyle) return;
    if (selectedStyle === 'custom' && !customStyle.trim()) {
      setError(t('errCustomStyle'));
      return;
    }
    // 평면도 모드 — 첫 장만, 스타일은 고른 칩(정리는 스타일 없음 → 'floorplan').
    const isPlan = selectedStyle === 'floorplan';
    const planMode = !isPlan ? null
      : planVariant === '3d' ? 'plan_3d'
      : planVariant === 'room' ? 'plan_room'
      : planCleanKind === 'color' ? 'plan_color' : 'plan_clean';
    const genImages = isPlan ? selectedImages.slice(0, 1) : selectedImages;
    const genStyle = isPlan && planVariant !== 'clean' ? planStyle : selectedStyle;
    if (planMode === 'plan_room') {
      // 칠한 사본을 올려야 해서 로그인 사용자만 (서버 작업 경로).
      if (!user || user.isAnonymous) { onSignInRequest?.(); return; }
      if (!planRoomMark && !additionalRequest.trim()) { setError(t('planRoomNeedPick')); return; }
    }
    // Block stacking another generation on top of one already in flight.
    if (pendingActive) return;
    // Credit gate — Pro 는 무제한이라 skip. Free / Guest 만 차감 / out-of-credits 모달.
    // 크레딧 1개 = 이미지 1장이므로 필요량은 사진 장수다. 0 개면 할 수 있는 게
    // 기다리기 / 초대뿐이라 모달을 띄우고, 모자라기만 하면 사진을 줄여서 어느
    // 장을 쓸지 고를 수 있으므로 숫자를 알려주고 멈춘다.
    if (creditsKnown) {
      // 생성 1장 값도 안 남았으면 (Gio 메시지 몇 개 분만 남은 경우 포함) 벽.
      if (credits.credits < creditPrice) {
        // 수익화 분기점 지표 — 크레딧이 벽이 된 순간의 대부분은 서버 402 가
        // 아니라 이 사전 검사에서 끝난다. MONETIZATION.md / admin 분기점 참고.
        logEvent(analytics, 'credit_exhausted', { surface: 'precheck' });
        onOutOfCredits?.();
        return;
      }
      if (credits.credits < genImages.length * creditPrice) {
        logEvent(analytics, 'credit_exhausted', { surface: 'precheck_short' });
        setError(t('errCreditsShort', { need: genImages.length * creditPrice, have: credits.credits }));
        return;
      }
    }

    if (selectedStyle === 'custom' && customStyle.trim()) {
      AIService.saveCustomStyleToHistory(customStyle.trim());
      setCustomStyleHistory(AIService.getCustomStyleHistory());
    }

    setCurrentStep('generate');
    setGenerationError(null);
    setInvalidSpaceError(false);
    onGenerationStart?.();
    let originalImageUrls = [];

    try {
      // The AI auto-detects whether the input is empty (→ staging) or
      // already furnished (→ redesign) and reports the result back as
      // `detectedMode`. We pass 'auto' as a hint and rely on the response.
      const exteriorOptions = selectedSpaceCategory !== 'interior'
        ? {
            ...(selectedSeason && selectedSeason !== 'none' ? { season: selectedSeason } : {}),
            ...(selectedTimeOfDay && selectedTimeOfDay !== 'none' ? { timeOfDay: selectedTimeOfDay } : {}),
          }
        : null;
      // mode 결정 — cleanup 카드 선택 시 cleanup_tidy / cleanup_empty 로,
      // layout 카드는 layout_flow / layout_spacious 로 백엔드에 분기.
      // 그 외엔 'auto' (백엔드 자동 redesign / staging 감지).
      let generationMode = 'auto';
      if (selectedStyle === 'cleanup') {
        generationMode = cleanupVariant === 'empty' ? 'cleanup_empty' : 'cleanup_tidy';
      } else if (selectedStyle === 'layout') {
        generationMode = layoutVariant === 'spacious' ? 'layout_spacious' : 'layout_flow';
      } else if (planMode) {
        generationMode = planMode;
      }
      const exteriorOpts = exteriorOptions && Object.keys(exteriorOptions).length ? exteriorOptions : null;

      // 로그인 사용자 — 서버 작업 (2.3.0). 사진만 올리고 서버가 끝까지 만든다.
      // 앱을 닫아도 결과가 남고 푸시가 온다. 게스트는 아래 동기 경로.
      if (user && !user.isAnonymous) {
        originalImageUrls = await Promise.all(
          genImages.map(img => AIService.uploadOriginalImage(img.blob))
        );
        const referenceImageUrl = selectedStyle === 'reference' && referenceImage?.blob
          ? await AIService.uploadReferenceImage(referenceImage.blob)
          : null;
        const roomOverlayUrl = planMode === 'plan_room' && planRoomMark
          ? await AIService.uploadEditOverlay(planRoomMark.blob)
          : null;
        const { designId } = await AIService.startDesignJob({
          originalImageUrls,
          referenceImageUrl,
          ...(roomOverlayUrl && { roomOverlayUrl }),
          style: genStyle,
          customStyleDescription: selectedStyle === 'custom' ? customStyle : '',
          additionalRequest,
          budget: selectedBudget,
          mode: generationMode,
          spaceCategory: selectedSpaceCategory,
          exteriorOptions: exteriorOpts,
          promptLang: locale.promptLang,
          lang,
        });
        const design = await AIService.waitForDesign(designId);
        if (design.status === 'invalid') {
          logEvent(analytics, 'invalid_space_rejected', { reason: design.invalidReason === 'NOT_FLOORPLAN' ? 'not_floorplan' : 'space' });
          const msg = design.invalidReason === 'NOT_FLOORPLAN' ? t('errNotFloorplan') : t('errInvalidSpace');
          setInvalidSpaceError(true);
          setGenerationError(msg);
          onGenerationError?.(msg);
          return;
        }
        if (design.status !== 'success') {
          logEvent(analytics, 'design_failed', { error_type: 'all_images_failed' });
          setGenerationError(t('generationFailedHint'));
          onGenerationError?.(t('generationFailedHint'));
          return;
        }
        setSavedDesignId(designId);
        setDesignResult({
          ...design,
          originalImages: genImages.map(img => img.previewUrl),
          originalImage: genImages[0].previewUrl,
        });
        logEvent(analytics, 'design_generated', { style: genStyle, budget: selectedBudget, mode: planMode || design.mode, space_category: selectedSpaceCategory, detected_input_type: design.detectedInputType || 'photo', photo_count: genImages.length, path: 'job' });
        setCurrentStep('result');
        onGenerationDone?.(designId);
        return;
      }

      const result = await AIService.generateDesign(
        genImages.map(img => img.blob),
        genStyle,
        customStyle,
        additionalRequest,
        locale.promptLang,
        selectedBudget,
        generationMode,
        selectedSpaceCategory,
        exteriorOptions && Object.keys(exteriorOptions).length ? exteriorOptions : null,
        // Reference Style 일 때만 reference image blob 전달.
        selectedStyle === 'reference' && referenceImage ? referenceImage.blob : null,
      );

      if (result.invalidSpace) {
        logEvent(analytics, 'invalid_space_rejected', { reason: result.invalidReason === 'NOT_FLOORPLAN' ? 'not_floorplan' : 'space' });
        const msg = result.invalidReason === 'NOT_FLOORPLAN' ? t('errNotFloorplan') : t('errInvalidSpace');
        setInvalidSpaceError(true);
        setGenerationError(msg);
        onGenerationError?.(msg);
        return;
      }

      // Every image came back empty — the server has already refunded the
      // credit, so don't save a half-broken design or congratulate the user
      // with an "Open" banner. Surface the retry UI instead.
      const allImagesFailed = (result.generatedImages || []).every(g => !g?.data);
      if (allImagesFailed) {
        logEvent(analytics, 'design_failed', { error_type: 'all_images_failed' });
        setGenerationError(t('generationFailedHint'));
        onGenerationError?.(t('generationFailedHint'));
        return;
      }

      // Guest credit decrement — logged-in users are gated server-side.
      // 성공한 이미지 수만큼 뺀다. 서버가 로그인 사용자에게 실패한 장수를
      // 환불해 주는 것과 같은 결과 (functions/index.js 의 failedCount).
      if (!user || user.isAnonymous) {
        const succeeded = (result.generatedImages || []).filter(g => g?.data).length;
        CreditsService.decrementGuest(succeeded || 1);
      }

      originalImageUrls = await Promise.all(
        genImages.map(img => AIService.uploadOriginalImage(img.blob))
      );

      const generatedImageUrls = await Promise.all(
        (result.generatedImages || []).map(genImg =>
          genImg?.data ? AIService.uploadDesignImage(genImg.data, genImg.mimeType) : Promise.resolve(null)
        )
      );

      // Reference Style 사용 시 reference 사진도 storage 에 저장 — 추후
      // use-this-style 로 다시 시작할 때 prefill 으로 같은 reference 사용 가능.
      let referenceImageUrl = null;
      if (selectedStyle === 'reference' && referenceImage?.blob) {
        try {
          referenceImageUrl = await AIService.uploadReferenceImage(referenceImage.blob);
          console.log('[reference upload] OK:', referenceImageUrl);
        } catch (err) {
          console.warn('[reference upload] FAILED:', err?.message);
        }
      } else if (selectedStyle === 'reference') {
        console.warn('[reference upload] skipped — no blob in referenceImage:', referenceImage);
      }

      const finalMode = (!planMode && result.detectedMode) || 'redesign';
      const designId = await AIService.saveDesign(
        { ...result, generatedImageUrls },
        originalImageUrls,
        genStyle,
        selectedStyle === 'custom' ? customStyle : null,
        additionalRequest || null,
        lang,
        selectedBudget,
        finalMode,
        null, // sourceDesignId
        selectedSpaceCategory,
        exteriorOptions && Object.keys(exteriorOptions).length ? exteriorOptions : null,
        referenceImageUrl,
        planMode || result.outputType || null, // 평면도를 일반 스타일로 올리면 서버가 3D 평면도로 만든다
      );
      setSavedDesignId(designId);

      setDesignResult({
        ...result,
        originalImages: genImages.map(img => img.previewUrl),
        originalImage: genImages[0].previewUrl,
        originalImageUrls,
        generatedImageUrls,
        lang,
        style: genStyle,
        outputType: planMode || result.outputType || null,
        customStyleInput: selectedStyle === 'custom' ? customStyle : null,
        additionalRequest: additionalRequest || null,
        budget: selectedBudget,
        mode: finalMode,
        spaceCategory: selectedSpaceCategory,
        exteriorOptions: exteriorOptions && Object.keys(exteriorOptions).length ? exteriorOptions : null,
        detectedInputType: result.detectedInputType || 'photo',
        floorplanRoomFocus: result.floorplanRoomFocus || null,
      });

      logEvent(analytics, 'design_generated', { style: genStyle, budget: selectedBudget, mode: planMode || finalMode, space_category: selectedSpaceCategory, detected_input_type: result.detectedInputType || 'photo', photo_count: genImages.length });
      setCurrentStep('result');
      onGenerationDone?.(designId);
    } catch (err) {
      console.error('Design generation failed:', err);
      logEvent(analytics, 'design_failed', { error_type: err.message?.includes('network') ? 'network' : 'ai' });
      try {
        const designId = await AIService.saveFailedDesign({
          originalImageUrls,
          style: selectedStyle,
          customStyleInput: selectedStyle === 'custom' ? customStyle : null,
        });
        await AIService.logError(err, {
          step: 'generateDesign',
          designId,
          style: selectedStyle,
          photoCount: selectedImages.length,
        });
      } catch {
        // logging failure shouldn't block showing the error
      }
      const msg = getErrorMessage(err);
      setGenerationError(msg);
      onGenerationError?.(msg);
      // Stay on generate step to show retry UI
    }
  };

  const resetApp = () => {
    selectedImages.forEach(img => URL.revokeObjectURL(img.previewUrl));
    setCurrentStep('upload');
    setSelectedImages([]);
    setSelectedBudget('mid');
    setSelectedSpaceCategory('interior');
    setSelectedSeason('none');
    setSelectedTimeOfDay('none');
    setSelectedStyle('');
    setCustomStyle('');
    setAdditionalRequest('');
    setPlanVariant('3d');
    setPlanCleanKind('line');
    setPlanRoomMark(null);
    setDesignResult(null);
    setError(null);
    setGenerationError(null);
    setInvalidSpaceError(false);
  };

  // Reset selected style when category changes — exterior/garden have closed
  // sets and don't include `custom`, so a stale interior style key would
  // crash StyleStep.
  const handleSpaceCategoryChange = (cat) => {
    if (cat === selectedSpaceCategory) return;
    setSelectedSpaceCategory(cat);
    setSelectedStyle('');
    setCustomStyle('');
    logEvent(analytics, 'space_category_selected', { spaceCategory: cat });
  };

  const handleSignIn = useCallback(() => {
    onSignInRequest?.(savedDesignId);
  }, [onSignInRequest, savedDesignId]);

  const isStepWithCta = currentStep === 'upload' || currentStep === 'style';

  return (
    <main className={`main${isStepWithCta ? ' home-with-sticky-cta' : ''}${currentStep === 'upload' ? ' home-step-upload' : ''}`}>
      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: '1rem', lineHeight: 1 }}>×</button>
        </div>
      )}

      {sourceName && currentStep !== 'result' && (
        <div className="source-banner">
          <i className="material-symbols-rounded">auto_awesome</i>
          <span>{t('sourceDesignBanner', { name: sourceName })}</span>
          <button className="source-banner-close" onClick={() => setSourceName(null)} aria-label="Clear">
            <i className="material-symbols-rounded">close</i>
          </button>
        </div>
      )}

      {galleryPickShown && !sourceName && (currentStep === 'upload' || currentStep === 'style')
        && selectedStyle === prefill.style && (selectedStyle !== 'custom' || customStyle === prefill.customStyle) && (
        <div className="source-banner">
          <i className="material-symbols-rounded">style</i>
          <span>{t('galleryPickBanner', {
            name: selectedStyle === 'custom'
              ? `"${customStyle.length > 40 ? `${customStyle.slice(0, 40)}…` : customStyle}"`
              : styleName(selectedStyle, selectedSpaceCategory),
          })}</span>
          <button
            className="source-banner-close"
            onClick={() => { setGalleryPickShown(false); setSelectedStyle(''); setCustomStyle(''); }}
            aria-label="Clear"
          >
            <i className="material-symbols-rounded">close</i>
          </button>
        </div>
      )}

      {isStepWithCta && (
        <StepProgress
          current={currentStep === 'upload' ? 1 : 2}
          total={2}
          credits={credits}
          onCreditsClick={onCreditsClick}
          onBack={currentStep === 'style' ? () => { setError(null); setCurrentStep('upload'); } : null}
        />
      )}

      {currentStep === 'upload' && (
        <>
          <UploadStep
            selectedImages={selectedImages}
            onAddImage={processAndAddImage}
            onRemoveImage={removeImage}
            onCameraClick={() => setShowCamera(true)}
            onContinue={() => { setError(null); logEvent(analytics, 'photo_uploaded', { photo_count: selectedImages.length }); setCurrentStep('style'); }}
            spaceCategory={selectedSpaceCategory}
            onSpaceCategoryChange={handleSpaceCategoryChange}
          />
          {/* 데스크탑 홈에 붙어 있던 CommunityFeed 제거 — 공개 피드를 접으면서
             (App.jsx 라우트 주석 참고) 여기만 남겨두면 텅 빈 채로 노출된다.
             컴포넌트는 살아 있으니 되살릴 땐 이 자리에 다시 넣으면 된다. */}
        </>
      )}

      {currentStep === 'style' && (
        <StyleStep
          selectedImages={selectedImages}
          selectedStyle={selectedStyle}
          onStyleSelect={(s) => { setSelectedStyle(s); setAdditionalRequest(''); logEvent(analytics, 'style_selected', { style: s }); }}
          onGenerate={generateDesign}
          creditsAvailable={creditsKnown ? credits.credits : null}
          creditPrice={creditPrice}
          onBack={() => { setError(null); setCurrentStep('upload'); }}
          customStyle={customStyle}
          onCustomStyleChange={setCustomStyle}
          referenceImage={referenceImage}
          onReferenceChange={setReferenceImage}
          cleanupVariant={cleanupVariant}
          onCleanupVariantChange={setCleanupVariant}
          layoutVariant={layoutVariant}
          onLayoutVariantChange={setLayoutVariant}
          planVariant={planVariant}
          onPlanVariantChange={(v) => { setPlanVariant(v); logEvent(analytics, 'plan_variant_selected', { variant: v }); }}
          planStyle={planStyle}
          onPlanStyleChange={setPlanStyle}
          planCleanKind={planCleanKind}
          onPlanCleanKindChange={setPlanCleanKind}
          planRoomMark={planRoomMark}
          onPlanRoomMarkChange={setPlanRoomMark}
          isGuest={!user || user.isAnonymous}
          additionalRequest={additionalRequest}
          onAdditionalRequestChange={setAdditionalRequest}
          customStyleHistory={customStyleHistory}
          spaceCategory={selectedSpaceCategory}
          selectedSeason={selectedSeason}
          onSeasonChange={(s) => { setSelectedSeason(s); logEvent(analytics, 'season_selected', { season: s }); }}
          selectedTimeOfDay={selectedTimeOfDay}
          onTimeOfDayChange={(s) => { setSelectedTimeOfDay(s); logEvent(analytics, 'time_of_day_selected', { time: s }); }}
          styleLocked={!!sourceName}
        />
      )}

      {currentStep === 'generate' && (
        <GenerateStep
          error={generationError}
          onRetry={generateDesign}
          onBack={invalidSpaceError
            ? () => { setGenerationError(null); setInvalidSpaceError(false); setCurrentStep('upload'); }
            : () => { setGenerationError(null); setCurrentStep('style'); }
          }
          showRetry={!invalidSpaceError}
          backLabel={invalidSpaceError ? 'pickAnotherPhoto' : null}
          pending={pending}
        />
      )}

      {currentStep === 'result' && (
        <ResultStep
          result={designResult}
          savedDesignId={savedDesignId}
          user={user}
          onReset={resetApp}
          onSignIn={user?.isAnonymous ? handleSignIn : null}
          onPendingStart={onGenerationStart}
          onPendingDone={onGenerationDone}
          onPendingError={onGenerationError}
        />
      )}

      {showCamera && (
        <CameraComponent
          onCapture={handleCameraCapture}
          onClose={() => setShowCamera(false)}
          onError={(msg) => { setShowCamera(false); setError(msg); }}
        />
      )}
    </main>
  );
}

// ─── Sign In Modal ─────────────────────────────────────────────────────────────
function SignInModal({ onSignIn, onClose, loading, error }) {
  const { t } = useLocale();
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card signin-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose} aria-label="Close">
          <i className="material-symbols-rounded">close</i>
        </button>
        <h3 className="signin-modal-title">{t('signIn')}</h3>
        <p className="signin-modal-desc">{t('signInDesc')}</p>
        {error === 'popupBlocked' && (
          <p className="signin-modal-error">{t('signInPopupBlocked')}</p>
        )}
        <button className="btn signin-google-btn" onClick={() => onSignIn('google')} disabled={loading}>
          <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
            <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
            <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" fill="#34A853"/>
            <path d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957C.347 6.175 0 7.55 0 9s.348 2.826.957 4.039l3.007-2.332z" fill="#FBBC05"/>
            <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z" fill="#EA4335"/>
          </svg>
          {t('signInGoogle')}
        </button>
        {/* Apple Sign-In — Android 에서는 Web OAuth flow 라 UX 가 좋지 않고 Apple
            Guideline 4.8 도 Android 정책엔 무관. 안드로이드 네이티브 앱에서는 hide. */}
        {!isAndroid() && (
          <button className="btn signin-apple-btn" onClick={() => onSignIn('apple')} disabled={loading}>
            <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
              <path d="M14.94 9.42c-.02-2.08 1.7-3.08 1.78-3.13-.97-1.42-2.49-1.61-3.03-1.63-1.29-.13-2.51.76-3.16.76-.66 0-1.66-.74-2.73-.72-1.4.02-2.7.82-3.42 2.07C2.94 9.31 4.04 13 5.45 14.99c.69.97 1.5 2.06 2.57 2.02 1.04-.04 1.43-.67 2.69-.67 1.25 0 1.61.67 2.71.65 1.12-.02 1.83-.99 2.51-1.97.79-1.13 1.12-2.23 1.13-2.29-.03-.01-2.16-.83-2.18-3.31zM12.85 3.4c.57-.7.96-1.66.85-2.62-.83.03-1.83.55-2.42 1.24-.53.61-.99 1.6-.86 2.54.92.07 1.86-.47 2.43-1.16z" fill="currentColor"/>
            </svg>
            {t('signInApple')}
          </button>
        )}
        <p className="signin-legal-text">
          <a href="/terms" target="_blank" rel="noopener noreferrer">{t('terms')}</a>
          {' · '}
          <a href="/privacy" target="_blank" rel="noopener noreferrer">{t('privacy')}</a>
        </p>
      </div>
    </div>
  );
}


// 라우트 변경 시 스크롤 위치 reset. 모바일에서 sticky header 가 이전 페이지의
// scroll 위치에 어긋나 보이던 문제 + 길게 스크롤한 페이지 → 다른 페이지로 갔다 돌아올 때
// 이전 위치 잔류하던 문제 해결.
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// 라우트 변경마다 screen_view — Firebase 의 Screens 리포트와 화면별 체류시간을
// 채운다. 웹은 JS SDK, 네이티브는 @capacitor-firebase/analytics 로 갈리지만
// logScreen 이 그 분기를 흡수한다 (src/firebase.js).
// 경로에 든 ID (디자인 id, handle 등) 는 :param 으로 뭉개서 화면 이름이
// 사용자 수만큼 늘어나지 않게 — 그러면 리포트가 못 쓰게 된다.
const SCREEN_PATTERNS = [
  // 언어별 랜딩은 /landing 하나로 모은다 — /admin 랜딩 집계(functions/ga.js LANDING_PATH)가 그 이름을 센다.
  [/^\/(fr|es|pt|de|it|ru|tr|el|ja|ko)$/, '/landing'],
  [/^\/designs\/[^/]+$/, '/designs/:id'],
  [/^\/s\/[^/]+$/, '/s/:id'],
  [/^\/c\/[^/]+$/, '/c/:id'],
  [/^\/u\/[^/]+$/, '/u/:handle'],
];
// 곧바로 다른 화면으로 넘어가는 자리('/' → /create, '/my' → /designs)는 화면이
// 아니다 — 잠깐 머문 경로는 세지 않는다 (0.4초). /admin 의 화면별 사용량이 이걸 쓴다.
function ScreenTracker() {
  const { pathname } = useLocation();
  useEffect(() => {
    const match = SCREEN_PATTERNS.find(([re]) => re.test(pathname));
    const tm = setTimeout(() => logScreen(match ? match[1] : pathname), 400);
    return () => clearTimeout(tm);
  }, [pathname]);
  return null;
}

// 라우트마다 <head> 의 canonical · og:url · title · robots 를 맞춘다 (SEO).
// 셸 index.html 은 어느 주소로 와도 같은 파일이라 canonical 이 늘 "/" 였다 —
// /gallery · /u/:handle · /s/:id 가 전부 "홈의 중복" 으로 신고되고 있었다.
// Google 은 렌더 후 head 를 읽으니 여기서 바꾸면 반영된다. origin 은 고정이다
// (web.app / firebaseapp.com 사본이 archelier.co 를 가리키게).
const SEO_ORIGIN = 'https://archelier.co';
const SEO_BASE_TITLE = typeof document !== 'undefined' ? document.title : 'archelier';
const SEO_TITLES = [
  [/^\/gallery$/, 'Interior, Exterior & Garden Style Gallery — archelier'],
  [/^\/gio$/, 'Gio, Your AI Space Designer — archelier'],
  [/^\/support$/, 'Support — archelier'],
  [/^\/privacy(-policy)?$/, 'Privacy Policy — archelier'],
  [/^\/terms$/, 'Terms of Service — archelier'],
  [/^\/delete-account$/, 'Delete Your Account — archelier'],
];
// 로그인해야 의미가 있는 화면 — 크롤러에겐 로그인 벽뿐이라 색인에서 뺀다
// (robots.txt 의 Disallow 와 짝).
const SEO_NOINDEX = /^\/(admin|account|bookmarks|designs|invite|s)(\/|$)/;
const SEO_BASE_DESC = typeof document !== 'undefined'
  ? document.querySelector('meta[name="description"]')?.getAttribute('content') : null;

function setHeadTag(selector, create, attr, value) {
  let el = document.head.querySelector(selector);
  if (!el) {
    if (value == null) return;
    el = create();
    document.head.appendChild(el);
  }
  if (value == null) el.remove();
  else el.setAttribute(attr, value);
}
const metaEl = (key, name) => () => { const m = document.createElement('meta'); m.setAttribute(key, name); return m; };

function SeoHead() {
  const { pathname } = useLocation();
  const { lang: appLang } = useLocale();
  useEffect(() => {
    // 언어 랜딩(/ko …)은 자기 주소가 canonical 이고 제목·설명도 그 언어로 — 정적 사본과 같게.
    // 마케팅 홈은 / 가 /landing 으로 넘어가므로 둘 다 / 로 모은다 (/ = 영어판 겸 x-default).
    const lpLang = landingLangOf(pathname);
    const path = pathname === '/landing' ? '/' : lpLang ? `/${lpLang}` : (pathname.replace(/\/+$/, '') || '/');
    const url = SEO_ORIGIN + path;
    setHeadTag('link[rel="canonical"]', () => Object.assign(document.createElement('link'), { rel: 'canonical' }), 'href', url);
    setHeadTag('meta[property="og:url"]', metaEl('property', 'og:url'), 'content', url);
    setHeadTag('meta[name="robots"]', metaEl('name', 'robots'), 'content', SEO_NOINDEX.test(path) ? 'noindex' : null);
    let title = SEO_TITLES.find(([re]) => re.test(path))?.[1] || SEO_BASE_TITLE;
    let desc = SEO_BASE_DESC;
    if (lpLang) {
      const { t } = makeTranslator(lpLang);
      title = t('lp.metaTitle');
      desc = t('lp.metaDescription');
    }
    document.title = title;
    if (desc) setHeadTag('meta[name="description"]', metaEl('name', 'description'), 'content', desc);
    document.documentElement.lang = lpLang || appLang || 'en';
  }, [pathname, appLang]);
  return null;
}

// 단일 스크롤 방향 broadcaster — body class (scroll-down/up/top) 로 모바일
// chrome (MobileHeader, sticky 필터 wrapper) 에 Instagram 식 auto-hide 신호.
// 모든 모바일 sticky bar 가 같은 방향 정보를 쓰므로 listener 한 번만.
function ScrollDirectionBodyClass() {
  const dir = useScrollDirection();
  useEffect(() => {
    const cls = document.body.classList;
    cls.toggle('scroll-down', dir === 'down');
    cls.toggle('scroll-up', dir === 'up');
    cls.toggle('scroll-top', dir === 'top');
  }, [dir]);
  return null;
}

// ─── iOS Redirect Handler (inside BrowserRouter) ──────────────────────────────
function AuthRedirectHandler({ onSignInSuccess }) {
  const navigate = useNavigate();
  useEffect(() => {
    const raw = localStorage.getItem('voda_redirect_pending');
    if (!raw) return;
    AuthService.handleRedirectResult().then(async result => {
      localStorage.removeItem('voda_redirect_pending');
      if (!result?.user) return;
      onSignInSuccess();
      try {
        const { designId } = JSON.parse(raw);
        if (designId) {
          await DesignService.claimDesign(designId).catch(() => {});
          navigate(`/designs/${designId}`);
        }
      } catch (e) { /* ignore */ }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

// ─── Universal / App Links handler (Sprint A 6단계) ───────────────────────────
// Subscribes to Capacitor's appUrlOpen event. When iOS Universal Links or
// Android App Links deliver an https://voda-7647c.web.app/<path> URL, we
// strip the origin and let react-router navigate inside the webview.
function NativeUrlHandler() {
  const navigate = useNavigate();
  // 알림 탭 → 라우터 (push-service.js). 웜 스타트는 이벤트, 콜드 스타트는 부팅
  // 직후 한 번 오는 탭이 리스너보다 먼저 왔을 수 있어 pending 을 한 번 비운다.
  useEffect(() => {
    if (!isNativeApp()) return undefined;
    const onRoute = (e) => { PushService.consumePendingNav(); if (e.detail) navigate(e.detail); };
    window.addEventListener(OPEN_ROUTE_EVENT, onRoute);
    PushService.initTapHandler().then(() => {
      const r = PushService.consumePendingNav();
      if (r) navigate(r);
    });
    return () => window.removeEventListener(OPEN_ROUTE_EVENT, onRoute);
  }, [navigate]);
  useEffect(() => {
    if (!isNativeApp()) return;
    let listener;
    let cancelled = false;
    (async () => {
      const { App: CapApp } = await import('@capacitor/app');
      if (cancelled) return;
      listener = await CapApp.addListener('appUrlOpen', (event) => {
        try {
          const url = new URL(event.url);
          // 부팅 때 한 번 도는 capturePendingReferral 로는 이 경로가 안 잡힌다
          // (앱이 이미 떠 있는 상태) — 여기서 직접 낚아챈다.
          ReferralService.capturePendingReferralFromUrl(event.url);
          const path = `${url.pathname}${url.search}${url.hash}`;
          if (path && path !== '/') navigate(path);
          else navigate('/');   // 루트 딥링크 — HomeRoute 가 ref 를 보고 분기
        } catch {
          /* ignore malformed URLs */
        }
      });
    })();
    return () => {
      cancelled = true;
      listener?.remove?.();
    };
  }, [navigate]);
  return null;
}

// ─── Root App with routing ─────────────────────────────────────────────────────
// Sticky banner for in-flight design generations. Shown at App level so it
// persists across routes — the user can browse the feed / community while
// the AI works, then tap "Open" once it's ready.
function PendingBanner({ pending, onDismiss }) {
  const { t } = useLocale();
  const navigate = useNavigate();
  const location = useLocation();
  if (!pending) return null;

  // HomePage 의 generate step 은 inline progress 카드를 자체 노출하므로
  // banner 가 중복. 그 외 페이지/step (DesignDetail 의 EditRegion, /feed 등)
  // 에선 in_progress 도 banner 로 알려줘야 — 안 그러면 사용자가 화면 멈춘
  // 줄 알게 됨.
  if (pending.status === 'in_progress') {
    // HomePage 자체에 진행 표시 (GenerateStep inline progress / 사용자가 만든
    // 새 디자인 흐름) 가 있어 banner 가 중복. home (`/`, `/create`) 에선
    // in_progress 숨기고 done / error 만 보임. 다른 페이지 (DesignDetail /
    // Feed / Settings 등) 에선 정상 표시 — EditRegion 흐름이 거기서 시작.
    if (location.pathname === '/' || location.pathname === '/create') return null;
    const progress = pending.progress ?? 0;
    return (
      <div className="pending-banner pending-banner-progress" role="status">
        <div className="pending-banner-row">
          <i className="material-symbols-rounded spin">autorenew</i>
          <div className="pending-banner-body">
            <p className="pending-banner-title">{t('generationInProgress')}</p>
            <p className="pending-banner-hint">{t('generationKeepBrowsing')}</p>
          </div>
        </div>
        <div className="pending-banner-bar">
          <div className="pending-banner-bar-fill" style={{ width: `${progress}%` }} />
        </div>
      </div>
    );
  }

  if (pending.status === 'done') {
    return (
      <div className="pending-banner pending-banner-done" role="status">
        <div className="pending-banner-row">
          <i className="material-symbols-rounded">check_circle</i>
          <div className="pending-banner-body">
            <p className="pending-banner-title">{t('generationDone')}</p>
            <p className="pending-banner-hint">{t('generationTapToOpen')}</p>
          </div>
          <button className="pending-banner-close" onClick={onDismiss} aria-label={t('close')}>
            <i className="material-symbols-rounded">close</i>
          </button>
        </div>
        <button
          className="btn btn-primary pending-banner-btn"
          onClick={() => { navigate(`/designs/${pending.designId}`); onDismiss(); }}
        >
          {t('open')}
        </button>
      </div>
    );
  }
  // error
  return (
    <div className="pending-banner pending-banner-error" role="alert">
      <i className="material-symbols-rounded">error</i>
      <div className="pending-banner-body">
        <p className="pending-banner-title">{t('generationFailed')}</p>
        {/* 실제 사유를 그대로 전달한다. 예전엔 generationFailedHint 를 하드코딩해서
           어떤 실패든 "크레딧은 차감되지 않았어요. 다시 시도해주세요." 만 나왔다 —
           사유를 정확히 판정해 놓고도 생성 화면을 벗어나면 그게 버려졌고, 사진이
           공간으로 안 읽힌 경우엔 "다시 시도" 가 틀린 안내였다 (같은 사진이면 같은
           결과). 2026-08 App Store 심사에서 리뷰어가 이 배너만 보고 2.1(a) 로
           신고했다. pending 은 처음부터 message 를 담고 있었다. */}
        <p className="pending-banner-hint">{pending.message || t('generationFailedHint')}</p>
      </div>
      <button className="pending-banner-close" onClick={onDismiss} aria-label={t('close')}>
        <i className="material-symbols-rounded">close</i>
      </button>
    </div>
  );
}

function App() {
  // `undefined` = auth resolving, `null` = anonymous/no user, object = signed in.
  // Components that conditionally render auth UI check this to skip the
  // brief sign-in → profile flash on every page load.
  const [user, setUser] = useState(undefined);
  const [signInModal, setSignInModal] = useState(false);
  const [signInLoading, setSignInLoading] = useState(false);
  const [signInError, setSignInError] = useState(null);
  const [creditModalOpen, setCreditModalOpen] = useState(false);
  // Stripe 크레딧 팩 결제에서 돌아오면 (?pack=paid|cancel) 표시만 지우고 그 화면 그대로 —
  // 잔액은 웹훅이 넣고 스냅샷으로 바뀐다. 확인 모달을 다시 띄우지 않는다.
  useEffect(() => {
    const url = new URL(window.location.href);
    const ret = url.searchParams.get('pack');
    if (!ret) return;
    logEvent(analytics, ret === 'paid' ? 'pack_purchased' : 'pack_purchase_cancelled', { product: 'stripe' });
    url.searchParams.delete('pack');
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
  }, []);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const pendingDesignId = useRef(null);

  // Background design generation. The HomePage owns the actual fetch + save
  // logic, but it reports lifecycle through these callbacks so the banner
  // can persist across navigation. Shapes:
  //   null
  //   { status: 'in_progress', progress: 0..99, startedAt: ms }
  //   { status: 'done', designId }
  //   { status: 'error', message }
  const [pendingDesign, setPendingDesign] = useState(null);
  const startGeneration = useCallback(() => {
    setPendingDesign({ status: 'in_progress', progress: 0, startedAt: Date.now() });
  }, []);
  const finishGeneration = useCallback((designId) => {
    setPendingDesign({ status: 'done', designId });
    // 만족 순간에 인앱 평점 요청 (네이티브 전용, 스로틀은 서비스가 관리).
    ReviewService.onGenerationSuccess();
  }, []);
  const failGeneration = useCallback((message) => {
    setPendingDesign({ status: 'error', message });
  }, []);
  const dismissPending = useCallback(() => setPendingDesign(null), []);

  // Splash 단계에 feed 첫 페이지 + 첫 6 이미지 prewarm — 사용자가 /feed 클릭
  // 시 캐시 hit + browser HTTP 캐시 hit 으로 깜빡임 없이 즉시 표시. localStorage
  // 영속이라 콜드 스타트 후에도 지난 세션 데이터로 즉시 렌더 (stale OK, 백그라운드
  // 갱신). Best-effort, 실패는 무시.
  useEffect(() => {
    if (readFeedCache()) return; // 이미 캐시 있으면 skip — CommunityFeed 가 mount 시 fresh fetch
    DesignService.getFeedDesigns({ pageSize: 12 }).then(({ designs }) => {
      writeFeedCache(designs);
      designs.slice(0, 6).forEach(d => {
        const url = gridImage(d);
        if (url) { const img = new Image(); img.src = url; }
      });
    }).catch(() => {});
  }, []);

  // User-specific prewarm — auth 해결 후 자기 designs 첫 페이지 + 이미지 6장.
  // /designs (My) 첫 진입 깜빡임 제거. 비로그인 / anonymous 는 skip.
  useEffect(() => {
    if (!user || user.isAnonymous) return;
    const uid = user.uid;
    const cacheKey = `userDesigns:${uid}:all:*:*`;
    if (QueryCache.get(cacheKey)) return;
    DesignService.getUserDesigns(uid, { pageSize: 18 }).then(({ designs }) => {
      QueryCache.set(cacheKey, { designs });
      designs.slice(0, 6).forEach(d => {
        const url = gridImage(d) || d.originalImageUrls?.[0];
        if (url) { const img = new Image(); img.src = url; }
      });
    }).catch(() => {});
  }, [user]);

  // Smooth fake progress while in-flight. Real progress isn't available
  // (Cloud Function is a single blocking call), but an ease-out curve over
  // an estimated 60s gives the user a clear "it's still working" signal.
  useEffect(() => {
    if (pendingDesign?.status !== 'in_progress') return;
    const id = setInterval(() => {
      setPendingDesign(prev => {
        if (!prev || prev.status !== 'in_progress') return prev;
        const elapsed = (Date.now() - prev.startedAt) / 1000;
        const t = Math.min(1, elapsed / 60);
        const eased = Math.floor(99 * (1 - Math.pow(1 - t, 1.8)));
        const next = Math.max(prev.progress, Math.min(99, eased));
        return next === prev.progress ? prev : { ...prev, progress: next };
      });
    }, 500);
    return () => clearInterval(id);
  }, [pendingDesign?.status]);

  // RevenueCat 초기화 — 앱 boot 시 한 번 (native + iOS/Android 만, 웹은 no-op).
  // SDK configure 가 끝나야 logIn 등이 동작하므로 auth subscribe 보다 먼저.
  useEffect(() => {
    RevenueCatService.init();
  }, []);

  useEffect(() => {
    const unsubscribe = AuthService.onAuthChange((u) => {
      setUser(u);
      setUserId(analytics, u?.isAnonymous ? null : (u?.uid ?? null));
      // RevenueCat 식별자 동기화 — Firebase UID 로 customer 매핑.
      // 비로그인 / anonymous → logOut (RC 의 anonymous customer 로 회귀).
      if (u && !u.isAnonymous) {
        RevenueCatService.login(u.uid);
        // 알림 허용은 온보딩 투어가 끝난 뒤에 묻는다 (Onboarding.jsx). 투어를
        // 로그인 전에 마쳤거나 예전에 본 사람은 여기서 — 이미 정했으면 묻지 않고
        // 허락된 기기만 토큰을 갱신한다.
        if (isOnboardingDone()) PushService.askPermission();
        else PushService.ensureRegistered();
      } else {
        RevenueCatService.logout();
      }
      // Run initializeUser once a day for signed-in users. 일일 크레딧 자체는
      // 서버가 소비 시점에 lazy reset 하므로 이 호출이 없어도 리셋은 되지만,
      // timezone 갱신과 referralCode / creditBonus backfill 이 여기서 일어난다.
      // 서버는 idempotent 라 marker 가 낡아도 요청 한 번 낭비될 뿐 위험하지 않다.
      //
      // 키 이름에 `credits_` 가 붙은 이유: 크레딧 모델 전환 배포 직후, 예전 키가
      // 오늘 날짜로 남아 있는 세션은 backfill 을 건너뛰고 legacy 필드만 가진
      // 채로 남는다. 키를 바꿔서 전원이 한 번은 반드시 다시 타게 한다.
      if (u && !u.isAnonymous) {
        const today = new Date().toISOString().slice(0, 10);
        const key = `voda_credits_initialized_${u.uid}`;
        if (localStorage.getItem(key) !== today) {
          localStorage.setItem(key, today);
          AuthService.initializeCredits(u).catch(() => { /* non-fatal */ });
        }
      }
    });
    return unsubscribe;
  }, []);

  // Welcome 온보딩 노출 조건. 가입자: 안 보임. 익명 + 마지막 노출 후 24시간
  // 경과 + 누적 ≤ 5회. Spotify / Notion 식 daily re-engagement 패턴 — 매번
  // 켤 때 무조건 노출하면 짜증나니 24h cooldown + 5회 hard cap.
  useEffect(() => {
    if (user === undefined) return;       // auth 아직 resolving
    if (user && !user.isAnonymous) return; // 가입자: 안 보임
    try {
      const LAST = 'archelier_welcome_v1_last';
      const CNT = 'archelier_welcome_v1_count';
      const count = Number(localStorage.getItem(CNT) || '0');
      if (count >= 5) return;
      const last = Number(localStorage.getItem(LAST) || '0');
      const ONE_DAY = 24 * 60 * 60 * 1000;
      if (count === 0 || (Date.now() - last) > ONE_DAY) {
        setWelcomeOpen(true);
      }
    } catch { /* localStorage 차단된 환경 — skip */ }
  }, [user]);

  const markWelcomeSeen = useCallback(() => {
    try {
      const CNT = 'archelier_welcome_v1_count';
      const count = Number(localStorage.getItem(CNT) || '0');
      localStorage.setItem(CNT, String(count + 1));
      localStorage.setItem('archelier_welcome_v1_last', String(Date.now()));
    } catch { /* skip */ }
  }, []);

  const handleWelcomeClose = useCallback(() => {
    markWelcomeSeen();
    setWelcomeOpen(false);
  }, [markWelcomeSeen]);

  const openSignIn = useCallback((designId = null) => {
    pendingDesignId.current = designId;
    setSignInError(null);
    // 사용자가 명시적으로 로그인 의도 있는 순간 → Welcome 으로 우회해서 trial
    // offer 강조 노출. 앱 launch (passive) 의 24h cooldown 과 무관 — sign-in
    // 의도가 가장 conversion 직전 순간이라 항상 보여줌. 단 anonymous 일 때만
    // (이미 가입자가 재로그인 trying 하는 케이스는 SignInModal 그대로).
    const isAnonymous = !user || user.isAnonymous;
    if (isAnonymous) {
      setWelcomeOpen(true);
    } else {
      setSignInModal(true);
    }
  }, [user]);

  const handleSignIn = useCallback(async (provider = 'google') => {
    const designId = pendingDesignId.current;
    setSignInLoading(true);
    setSignInError(null);
    try {
      let claimNeeded = false;
      const beforeSwitch = designId ? async () => {
        await DesignService.markForClaim(designId);
        claimNeeded = true;
      } : undefined;

      if (provider === 'apple') {
        await AuthService.signInWithApple(beforeSwitch);
      } else {
        await AuthService.signInWithGoogle(beforeSwitch);
      }
      setUser(AuthService.currentUser);
      // 게스트 → 가입은 linkWithCredential 이라 uid 가 그대로여서 onAuthStateChanged 가
      // 안 울린다 — RC 도 여기서 직접 잇는다. 빼먹으면 익명 ID 로 결제된다.
      RevenueCatService.login(AuthService.currentUser?.uid);
      logEvent(analytics, 'sign_in_completed', { provider });

      if (claimNeeded && designId) {
        await DesignService.claimDesign(designId).catch(() => {});
      }
      setSignInModal(false);
      // Welcome 화면에서 시작한 sign-in 도 같은 핸들러 — 성공 시 닫고 count 영구화.
      // 가입자는 다시 welcome 노출 안 함 (useEffect 의 isAnonymous 체크).
      markWelcomeSeen();
      setWelcomeOpen(false);
    } catch (err) {
      const current = AuthService.currentUser;
      if (current && !current.isAnonymous) {
        // Auth succeeded despite COOP popup-close error
        if (designId) await DesignService.claimDesign(designId).catch(() => {});
        setSignInModal(false);
      } else if (err.code === 'auth/popup-blocked') {
        setSignInError('popupBlocked');
      }
    } finally {
      setSignInLoading(false);
    }
  }, [markWelcomeSeen]);

  return (
    <BrowserRouter>
      <JsSplash />
      <div className="app">
        <Header
          user={user}
          onSignInRequest={openSignIn}
          onCreditsClick={() => setCreditModalOpen(true)}
        />
        <MobileHeader />
        <AuthRedirectHandler onSignInSuccess={() => setUser(AuthService.currentUser)} />
        <NativeUrlHandler />
        <ScrollToTop />
        <ScreenTracker />
        <SeoHead />
        <ScrollDirectionBodyClass />
        <Routes>
          {/* Mobile: '/' redirects to '/create' (create-first UX). Desktop: '/' renders Create/HomePage.
             Create flow is also accessible via '/create' for explicit mobile navigation. */}
          <Route path="/" element={<HomeRoute
            user={user}
            onSignInRequest={openSignIn}
            onOutOfCredits={() => setCreditModalOpen(true)}
            onCreditsClick={() => setCreditModalOpen(true)}
            pendingActive={pendingDesign?.status === 'in_progress'}
            pending={pendingDesign}
            onGenerationStart={startGeneration}
            onGenerationDone={finishGeneration}
            onGenerationError={failGeneration}
            onDismissPending={dismissPending}
          />} />
          <Route path="/create" element={<RequireApp><HomePage
            user={user}
            onSignInRequest={openSignIn}
            onOutOfCredits={() => setCreditModalOpen(true)}
            onCreditsClick={() => setCreditModalOpen(true)}
            pendingActive={pendingDesign?.status === 'in_progress'}
            pending={pendingDesign}
            onGenerationStart={startGeneration}
            onGenerationDone={finishGeneration}
            onGenerationError={failGeneration}
            onDismissPending={dismissPending}
          /></RequireApp>} />
          <Route path="/designs" element={<MyDesigns user={user} />} />
          <Route path="/designs/:id" element={<DesignDetail user={user} openSignIn={openSignIn} onOutOfCredits={() => setCreditModalOpen(true)} onGenerationStart={startGeneration} onGenerationDone={finishGeneration} onGenerationError={failGeneration} />} />
          <Route path="/s/:id" element={<ShareView user={user} openSignIn={openSignIn} />} />
          {/* 구독 판매 중단 — /pricing 은 Account 로 흘려보낸다. Pricing 컴포넌트와
             Stripe / RevenueCat 배선은 지우지 않았다 (기존 결제자 관리 + 언제든
             되살릴 수 있게). 다시 팔려면 이 줄만 원복. */}
          <Route path="/pricing" element={<Navigate to="/account" replace />} />
          <Route path="/account" element={<Account user={user} onSignInRequest={openSignIn} onCreditsClick={() => setCreditModalOpen(true)} />} />
          <Route path="/account/blocked" element={<BlockedUsers user={user} onSignInRequest={openSignIn} />} />
          <Route path="/invite" element={<Invite user={user} onSignInRequest={openSignIn} />} />
          <Route path="/bookmarks" element={<Bookmarks user={user} onSignInRequest={openSignIn} />} />
          {/* 공개 피드는 당분간 접어 둔다 — 올리는 사람이 거의 없어서 빈 화면이
             오히려 "아무도 안 쓰는 앱" 처럼 보였다. CommunityFeed / FeedCard /
             DesignService.getFeedDesigns 는 그대로 남아 있으니 되살릴 땐 이
             라우트만 복구하고 MobileTabBar 에 탭을 다시 넣으면 된다.
             옛 링크·북마크는 /my 로 흘려보낸다. */}
          <Route path="/feed" element={<Navigate to="/my" replace />} />
          <Route path="/my" element={<Navigate to="/designs" replace />} />
          <Route path="/gallery" element={<Gallery onCreditsClick={() => setCreditModalOpen(true)} />} />
          <Route path="/gio" element={<Gio user={user} onSignInRequest={openSignIn} onOutOfCredits={() => setCreditModalOpen(true)} />} />
          <Route path="/u/:handle" element={<Profile user={user} onSignInRequest={openSignIn} />} />
          <Route path="/c/:id" element={<CollectionPage user={user} />} />
          <Route path="/landing" element={<Landing />} />
          {/* 언어별 랜딩 (/ko, /ja …) — 같은 화면을 그 언어로. 정적 사본은 scripts/prerender-landing.mjs. */}
          {LANDING_LANG_ROUTES.map((l) => <Route key={l} path={`/${l}`} element={<Landing lang={l} />} />)}
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          {/* 정적 /privacy-policy.html 의 짝. 옛 service worker 를 물고 있는
              client 는 이 주소에서도 SPA 껍데기를 받는데, 라우트가 없으면
              빈 화면이 된다. 같은 컴포넌트를 물려 둔다. */}
          <Route path="/privacy-policy" element={<Privacy />} />
          <Route path="/support" element={<Support />} />
          <Route path="/delete-account" element={<DeleteAccount />} />
          <Route path="/admin" element={<Admin user={user} />} />
        </Routes>
        {signInModal && (
          <SignInModal
            onSignIn={handleSignIn}
            onClose={() => setSignInModal(false)}
            loading={signInLoading}
            error={signInError}
          />
        )}
        {welcomeOpen && (
          <WelcomeGate>
            <WelcomeScreen
              onClose={handleWelcomeClose}
              onSignIn={handleSignIn}
              loading={signInLoading}
              error={signInError}
            />
          </WelcomeGate>
        )}
        <CreditModal open={creditModalOpen} onClose={() => setCreditModalOpen(false)} onSignInRequest={openSignIn} />
        <PendingBanner pending={pendingDesign} onDismiss={dismissPending} />
        <MobileTabBar />
        <Onboarding blocked={user === undefined || welcomeOpen || signInModal} />
        <AnnouncementModal user={user} blocked={welcomeOpen || signInModal || creditModalOpen} />
      </div>
    </BrowserRouter>
  );
}

export default App;
